import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { getMaxPointSize } from '../scene-manager.js';
import { getCrowdAtlas } from '../crowd-atlas.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';
import { preloadBayfrontSkyline, loadBayfrontSkyline } from '../bayfront-skyline-assets.js';
import { laserClampChunk, laserBillboardChunk, laserBeamFragmentShader } from '../lighting/laser-array.js';
import { loadArtworkTexture } from '../artwork-texture.js';
import { PyroLayer, GROUND_Y as PYRO_GROUND_Y, NOZZLE_TILT as PYRO_NOZZLE_TILT, V_WALL as PYRO_V_WALL, BEAT_PARTICLES as PYRO_BEAT_PARTICLES } from '../particle-layers/pyro-layer.js';
import { ConfettiLayer } from '../particle-layers/confetti-layer.js';

// Shared-rig contract: no rig. The light in this scene is the structure's
// own - its facets, its pixel bars, its lyres and lasers - and the crowd's.
const RIG = 'forbidden';
export { RIG };

export const PROFILE = {
  id: 'bayfront',
  family: 'stage',
  danceability: 0.92,
  energyLevel: 0.92,
  bpmMin: 122,
  bpmMax: 180,
  movement: 0.80,
  tonality: 0.55,
  prefers: { gridStrength: [0.65, 1.0], lowEnd: [0.50, 1.0] },
  enabled: true
};

// Bayfront: the main stage of an electronic festival on a bay - a central
// DIAMOND of LED surrounded by facets at different depths, every edge of
// every facet underlined by a bar of pixels, lyres to the sky, lasers over
// the crowd, pyro at the lip, and a city skyline mirrored in the water
// behind. EDM, big room, trance, festival house.
//
// IT GREW OUT OF CROWDWAVE, which stays as its foreground: the crowd in
// perspective to the horizon, the phones, the waves of raised arms and the
// night sky are CrowdWave's, moved a few files down. What changed is the
// point of view - the camera is now IN the crowd looking at the stage, so
// the crowd is seen from behind and fills the bottom quarter - and what the
// crowd is looking at: the small stage on the horizon became a sculpture of
// light filling the upper three quarters of the frame.
//
// THE STRUCTURE IS A NETWORK, and that is the decisive detail. Its facets
// SHARE their vertices - the octagon's panels meet, a wing is one strip of
// facets, a column is one chain - and the pixel bars run along every edge of
// that shared graph. The chases are runners on the graph: a point of light
// runs along an edge and, at a shared vertex, picks another edge and goes
// on, across the facet boundary. That is what makes forty quads at forty
// depths read as ONE crystal rather than as forty screens.
//
// WHAT IS REUSED, UNMODIFIED: MainStage's LED cell pipeline (its cell mask,
// jitter and seams, duplicated here as text - the file exports none of it),
// BeamWall's choreography (its pattern functions, notched on the beat with a
// short ease and a two-beat crossfade, duplicated with the base axis turned
// to the sky), LaserArray's billboard and beam fragment (imported), and
// PyroLayer in its world-space mode (imported).

// --- the camera: in the crowd, looking up at the stage ---------------------
const _v3 = new THREE.Vector3();
const CAMERA_POS = [0, 3.0, 0];
const CAMERA_PITCH_DEG = 13;          // the brief's 12, plus a hair for the emblem
const STAGE_Z = -44;                   // the front plane of the structure
const DECK_Y = 2.0;                    // the stage floor
// --- the crowd (CrowdWave's, closer and shallower) -------------------------
const Z_NEAR = -3.0;
const Z_FAR = -38;
const PERSON_HEIGHT = 1.7;
const SCALE_JITTER = 0.15;
const ROW_JITTER = 0.30;
const SPAN_OVERSHOOT = 1.20;
const OVERLAP = 0.30;
const BODY_LUMINANCE = 0.02;
const RIM_INTENSITY = 0.70;
const RIM_FAR_FADE = 0.55;
const SPRITE_BOTTOM_FADE = 0.12;
const SWAY_PERIOD = [1.5, 3.0];
const SWAY_AMOUNT = 0.045;
const BOB_AMOUNT = 0.025;
const HAZE_K = 0.030;
const WAVE_BEATS = 4;                  // a front crosses the crowd in four beats
const WAVE_HOLD_BEATS = 1;             // one wave per BAR now, so a full-beat hold reads
const WAVE_SLOTS = 4;
const WAVE_JUMP = 0.09;
const PHONE_FRACTION = 1 / 3;
const PHONE_COLOR = [0.78, 0.88, 1.0];
const PHONE_SIZE_PX = [11, 18];
const PHONE_LIT_RANGE = [0.20, 0.60];
const PHONE_ZONE_STEPS = 4;
// --- the structure ------------------------------------------------------------
const DIAMOND_CENTER_Y = 21;
const DIAMOND_HALF_H = 6.7;            // 22% of the frame's height at this depth
const DIAMOND_HALF_W = 6.0;
const DIAMOND_TILT = 0.9;              // top toward the audience, world units
const OCTAGON_R_IN = 8.6;
const OCTAGON_R_OUT = 12.4;
const OCTAGON_DEPTH = 2.0;
const WING_FACETS = 7;
const WING_DEPTH_MAX = 8.0;
const COLUMN_PANELS = 6;
const EMBLEM_Y = 41;                   // lowered from 45, and the crown sits tighter on it
const STRUCTURE_HALF_W = 44;           // the mapped surface's bounding box
const STRUCTURE_TOP = 47;
const LED_CELLS_PER_UNIT = 1.6;        // the pixel pitch of a facet
// LUMINANCE, and under the bloom threshold: the facets never bloom. Only the
// beams, the lasers and the pixel bars do - measured at 0.85 on the maximum
// channel the structure was one flat white slab.
const FACET_PEAK = 0.75;
const FACET_DEPTH_DARKEN = 0.30;       // the far facets, 30% darker: the relief
// The base: a continuous LED wall from the deck to the octagon's foot, the
// DJ booth on a podium in its middle, and two blocks of panels rising from it
// to the wings. Without it the structure hung in the air over the crowd.
// The socle is a TRAPEZOID, wider at the deck than at its top, cut into
// chevron facets whose depths alternate by a unit, so it speaks the wings'
// geometry rather than sitting under them as a slab. Its top points are the
// wings' and the blocks' own bottom vertices, so the graph is one.
const SOCLE_TOP = 9.4;                 // 12% of the frame's height at this depth
const SOCLE_BASE_HALF_W = 46;
const BLOCK_TOP = 15.5;
// The DJ podium: two units toward the audience, the nearest thing to the
// camera, a tenth of the frame's width, its own slanted LED panels either
// side.
const PODIUM_HALF_W = 6.0;
const PODIUM_H = 2.0;
const PODIUM_OUT = 2.5;
const BOOTH_Z = STAGE_Z + 1.4;
// --- the pixel bars -----------------------------------------------------------
const BAR_PIXELS = 16;
const BAR_LIFT = 0.25;                 // toward the audience, off the facet
const BAR_PIXEL_PX = 7.5;              // at the stage's depth, 1080p
const CHASE_RUNNERS = 24;
const CHASE_EDGES_PER_BEAT = 2.0;
const CHASE_DECAY = 7.0;               // the trail: exp(-dt * k)
const STROBE_MS = 40;
const STROBE_HEAT = 22.0;              // StrobeWall's filament: ~45 ms up
const STROBE_COOL = 5.5;               //                        ~180 ms down
const BAR_PATTERNS = ['outline', 'network', 'alternate', 'flat'];
// --- the lyres (BeamWall's choreography, aimed at the sky) -------------------
const LYRE_COUNT = { 2: 32, 1: 20, 0: 16 };
const LYRE_BEAM_LEN = 46;
const LYRE_NEAR_PX = 14;
const LYRE_FAR_PX = 54;
const LYRE_EXT = 0.040;                // the brief's haze
const LYRE_CORE_W = 0.44;
const LYRE_HALO_W = 0.28;
const LYRE_HOT_MIX = 0.70;
const LYRE_PEAK = 0.92;
const MOVE_MS = 110;
const PATTERN_FADE_BEATS = 2;
const LYRE_PAT = { fan: 0, wave: 1, ripple: 2, converge: 3, rain: 4, chase: 5, storm: 6 };
const LYRE_CYCLE = [1, 0, 2, 5, 6, 3];
// --- the lasers -------------------------------------------------------------------
const LASER_EMITTERS = 3;
const LASER_PER_EMITTER = 12;
const LASER_LEN = 70;
const LASER_HALF_NEAR = 0.035 / 2;
const LASER_HALF_FAR = 0.075 / 2;
const LASER_HOT_MIX = 0.95;
const LASER_PEAK = 0.92;
// Lasers go DOWN over the crowd, from the top of the structure toward the
// camera. SCAN sweeps the sheet from a shallow angle to one that nearly
// reaches the camera and back, once a bar; FAN holds a flat fan at one angle.
const LASER_PITCH_FAN = 22 * Math.PI / 180;
const LASER_PITCH_SCAN = [12 * Math.PI / 180, 38 * Math.PI / 180];
const LASER_SHEET_K = 0.06;            // how thick the sheet is on the crowd
// --- the pyro ---------------------------------------------------------------------
const PYRO_SCALE = 10.0;               // world units per simulation unit
// The layer sizes a grain as uPixel * refDepth / viewDepth: the reference
// depth MUST be set to the plane's own view depth (setWorld's last argument)
// or every grain is drawn at 1/44 of its size and the fire is invisible -
// that is why the demo fired 214 gerbs nobody saw on 2026-09-26.
const PYRO_MAX = 3000;
// All six clear of the podium's slanted sides (they reach x = 12 behind the
// pyro plane) and inside the CO2 cannons.
const PYRO_NOZZLE_X = [-22, -17.5, -13, 13, 17.5, 22];
// --- the confetti ----------------------------------------------------------
// particle-layers/confetti-layer.js (the former Confetti visualizer's engine,
// 2026-10-12): four cannons on the pyro nozzles' places, paper at the stage's
// scale (unit 7: the lip is 44 units from the lens), lit by six VIRTUAL
// beams fanned from six of the lyres' mounts toward the crowd - the real
// lyres' directions live in a vertex shader and are not known on the CPU.
// One cannon per bar in a chorus, two with serpentines on a phrase, all
// four on the drop. Tiers with pyro only.
const CONFETTI_MAX = { 1: 3000, 2: 6000 };
const CONFETTI_UNIT = 7.0;
const CONFETTI_CANNONS = [0, 2, 3, 5];
const CONFETTI_LIGHTS = 6;
const PYRO_V_SHORT = [1.2, 3.4];       // wide, so the base stays lit while the top climbs
// The layer's gerb is a 120 ms shot: from forty-two units away one shot is
// a puff that lifts off as a block. A fountain here is a TRAIN of shots from
// the same nozzle, one every gap, so the base burns while the first climb.
const PYRO_TRAIN_SHOTS = 5;
const PYRO_TRAIN_GAP = 0.12;           // seconds, the layer's emission window
// A grain is a point of light forty-two units away; Pyro's law gives it
// under a pixel at 720 lines and the fountain vanishes into the facets'
// grid. Never under 1.4 device pixels, and half again what the law says.
const PYRO_GRAIN_MIN = 1.4;
const PYRO_GRAIN_GAIN = 1.5;
// The facets go to 40% for two seconds while the wall of fire burns (the
// wall is white on the drop's white socle otherwise), like MainStage's wall.
const PYRO_WALL_DIM = 0.40;
const PYRO_WALL_DIM_SECONDS = 2.0;
const PYRO_WALL_PER_NOZZLE = 320;
const CO2_PUFFS = 90;                  // per cannon
const CO2_SECONDS = 0.6;
const CO2_X = 30;                      // outside the outer nozzles, or the jet buries the gerb
// --- the city ----------------------------------------------------------------------
const CITY_DEPTH = 96;                 // inside the camera's far plane (100)
// THE PLATE: the Miami photograph (tools/build-bayfront-skyline.mjs) on one
// plane beyond the wings (STAGE_Z - WING_DEPTH_MAX = -52) and short of the
// sky plane. Its sky is cut out (alpha, discarded), its ground line sits on
// the scene's floor line, the bay below it is drawn by the plate itself as
// a streaked mirror of the city. A beam's start decides which side of the
// plate it is on: the plate WRITES DEPTH where it is opaque, so a beam from
// the stage is occluded exactly where it passes behind a tower - cranes
// included - and never where it is nearer than the plate.
const PLATE_DEPTH = 64;
const PLATE_WIDTH_MARGIN = 1.18;       // of the frustum's width at that depth, aspect included
const PLATE_WATER = 0.55;              // the quad extends this fraction of the image's height below it
const PLATE_CONTRAST = 0.30;           // the atmospheric haze takes this much contrast
const PLATE_TINT = 0.30;               // the calibration tint, at most
const PLATE_SCENE_LIGHT = 0.15;        // scene light on the low facades
const PLATE_BLUR_PX = 1.0;
const HAZE_LAYER_DEPTH = 57;           // between the wings and the plate
const HAZE_LAYER_BOTTOM = 0.32;        // alpha at the ground line - the junction's veil; the plate carries its own 30%
const HAZE_LAYER_TOP = 0.04;
const CITY_FLICKER_EVERY = 6;          // one tower in six twinkles
const CITY_FLICKER = 0.10;             // +-10% over 8 beats
const CITY_BUILD_FLOOR = 0.10;         // the towers go out to this during the build
const CITY_BUILD_BEATS = 32;           // the build's dimming runs over this many beats at most
const CITY_DROP_LEVEL = 1.3;           // one bar after the drop
const CITY_WAVE_GAIN = 0.45;           // the phrase's wave, left to right in one beat
const CITY_LIT_MAX = 2.0;              // the lit texture's byte range
const SAFE_FLOOR = 0.50;
const SAFE_SHARED_FLOOR = 0.32;
const FALLBACK_COLORS = [0xff2fb0, 0x2fd8ff, 0xffb02f];
const HAZE_FALLBACK = 0x1c2440;
const CONTENT = { artwork: 0, solid: 1, stripes: 2, pulse: 3, noise: 4 };
const CONTENT_CYCLE = [0, 2, 1, 3];   // the cover first, when there is one
const SEGMENTS = ['intro', 'verse', 'build', 'drop', 'chorus', 'outro'];
const DROP_BARS = 2;
const FULL_AT_PROGRESS = 0.40;         // without an analysis: everything from here

const TIER_LAYOUT = {
  0: { crowd: 800, rows: 20, facets: 24, bars: false, lasers: false, pyro: false, co2: false, lyres: 16 },
  1: { crowd: 1300, rows: 25, facets: 36, bars: true, lasers: true, pyro: true, co2: false, lyres: 20 },
  2: { crowd: 2600, rows: 25, facets: 99, bars: true, lasers: true, pyro: true, co2: true, lyres: 32 }
};
const DEFAULT_TIER = 2;

function clamp01(x) { return Math.max(0, Math.min(1, x)); }
function urlParam(name) {
  if (typeof location === 'undefined') return null;
  return new URLSearchParams(location.search).get(name);
}

// =============================================================================
// SHADER CHUNKS
// =============================================================================

const SAFE_REMAP = `
  float safeRemap(float a) {
    return ${SAFE_FLOOR.toFixed(2)} + (a - ${SAFE_SHARED_FLOOR.toFixed(2)})
         * ${((1 - SAFE_FLOOR) / (1 - SAFE_SHARED_FLOOR)).toFixed(6)};
  }
`;

// The safe zone read per FRAGMENT from the pixel's own position. A quad that
// is not parallel to the image plane interpolates a per-vertex box into
// something that is not a box, and the facets are all tilted.
const SAFE_FRAGMENT_CHUNK = `
  uniform float uAspect;
  uniform vec2 uSafeCenter;
  uniform vec2 uSafeHalfSize;
  uniform float uSafeFeather;
  uniform vec2 uResolution;
  ${SAFE_REMAP}
  float safeHere() {
    vec2 ndc = (gl_FragCoord.xy / uResolution) * 2.0 - 1.0;
    vec2 s = vec2(ndc.x * uAspect, ndc.y);
    vec2 q = abs(s - uSafeCenter) - uSafeHalfSize;
    float d = length(max(q, 0.0)) + min(max(q.x, q.y), 0.0);
    return safeRemap(mix(${SAFE_SHARED_FLOOR.toFixed(2)}, 1.0, smoothstep(0.0, uSafeFeather, d)));
  }
`;

const HAZE_CHUNK = `
  float hazeAmount(float depth) {
    float f = ${HAZE_K.toFixed(4)} * depth;
    return 1.0 - exp(-f * f);
  }
`;

const HASH_CHUNK = `
  float hash21(vec2 p) {
    p = fract(p * vec2(123.34, 456.21));
    p += dot(p, p + 45.32);
    return fract(p.x * p.y);
  }
  float h11(float n) { return fract(sin(mod(n, 6283.1853)) * 43758.5453); }
`;

// --- the crowd (CrowdWave's chunks) -------------------------------------------
const WAVE_CHUNK = `
  uniform vec4 uWave[${WAVE_SLOTS}];
  uniform float uWaveSeconds;
  uniform float uHoldSeconds;

  float waveCoord(float mode, vec2 axis) {
    float u0 = axis.x;
    float ux = clamp(axis.y * 0.5 + 0.5, 0.0, 1.0);
    if (mode < 0.5) return u0;
    if (mode < 1.5) return 1.0 - u0;
    if (mode < 2.5) return ux;
    if (mode < 3.5) return 1.0 - ux;
    if (mode < 4.5) return 0.5 * (u0 + ux);
    return min(1.0, length(vec2(axis.y, u0 * 2.0 - 1.0)) * 0.7071);
  }

  float waveRaise(float t, vec2 axis) {
    float raise = 0.0;
    for (int i = 0; i < ${WAVE_SLOTS}; i++) {
      vec4 w = uWave[i];
      if (w.x < 0.0) continue;
      float arrival = w.x + waveCoord(w.y, axis) * uWaveSeconds;
      float age = t - arrival;
      if (age < 0.0 || age > uHoldSeconds) continue;
      raise = max(raise, 1.0 - smoothstep(0.66, 1.0, age / uHoldSeconds));
    }
    return raise;
  }
`;

const MOTION_CHUNK = `
  vec2 crowdMotion(float t, float period, float seed, float amp, float bob) {
    float ph = t * 6.2831853 / period + seed * 6.2831853;
    return vec2(sin(ph) * amp, abs(sin(ph * 0.5)) * bob);
  }
`;

// Seen from BEHIND here, so the atlas is read the way MainStage reads it.
const ATLAS_UV_CHUNK = `
  vec4 frameRect(float index, vec4 atlas) {
    float col = mod(index, atlas.w);
    float row = floor(index / atlas.w);
    float u0 = col * atlas.x + atlas.y + atlas.z;
    float u1 = (col + 1.0) * atlas.x - atlas.y - atlas.z;
    float v1 = 1.0 - (row * atlas.x + atlas.y) - atlas.z;
    float v0 = 1.0 - ((row + 1.0) * atlas.x - atlas.y) + atlas.z;
    return vec4(u0, v0, u1, v1);
  }
`;

const crowdVertexShader = `
  precision highp float;
  attribute vec3 iPos;
  attribute float iHeight;
  attribute float iSpriteCalm;
  attribute float iSpriteUp;
  attribute float iSeed;
  attribute float iPeriod;
  attribute vec2 iAxis;
  uniform float uTime;
  uniform float uSway;
  uniform float uAllUp;
  uniform vec4 uAtlasGrid;
  uniform float uPixelScale;
  uniform vec4 uLaserSheet;   // origin y, pitch, gain, origin z
  varying vec2 vUv;
  varying float vSafe;
  varying float vStep;
  varying float vHaze;
  varying float vRim;
  varying float vQuadY;
  varying float vLaser;
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_REMAP}
  ${HAZE_CHUNK}
  ${WAVE_CHUNK}
  ${MOTION_CHUNK}
  ${ATLAS_UV_CHUNK}
  void main() {
    float raise = max(waveRaise(uTime, iAxis), uAllUp);
    float sprite = raise > 0.5 ? iSpriteUp : iSpriteCalm;
    vec4 rect = frameRect(sprite, uAtlasGrid);
    vUv = vec2(mix(rect.x, rect.z, position.x + 0.5), mix(rect.y, rect.w, position.y));
    vec2 m = crowdMotion(uTime, iPeriod, iSeed, iHeight * ${SWAY_AMOUNT.toFixed(3)} * uSway, iHeight * ${BOB_AMOUNT.toFixed(3)});
    vec3 world = iPos;
    world.x += m.x + position.x * iHeight;
    world.y += m.y + raise * iHeight * ${WAVE_JUMP.toFixed(3)} + position.y * iHeight;
    vec4 mv = modelViewMatrix * vec4(world, 1.0);
    gl_Position = projectionMatrix * mv;
    vSafe = safeRemap(safeZoneAttenuation(gl_Position));
    float depth = max(-mv.z, 0.001);
    vHaze = hazeAmount(depth);
    vRim = mix(1.0, ${RIM_FAR_FADE.toFixed(2)}, clamp(depth / ${(-Z_FAR).toFixed(1)}, 0.0, 1.0));
    vQuadY = position.y;
    // The laser sheet passing overhead: distance of this person's head to
    // the sheet's plane, and a glow where it is close - the crowd lit under
    // the sweep.
    vec3 n = vec3(0.0, cos(uLaserSheet.y), sin(uLaserSheet.y));
    float dsh = dot(vec3(iPos.x, iPos.y + iHeight, iPos.z) - vec3(0.0, uLaserSheet.x, uLaserSheet.w), n);
    vLaser = uLaserSheet.z * exp(-dsh * dsh * ${LASER_SHEET_K.toFixed(3)});
    float screenPx = iHeight * uPixelScale / max(gl_Position.w, 0.001);
    float contentUv = uAtlasGrid.x - 2.0 * uAtlasGrid.y;
    vStep = max(contentUv / max(screenPx, 1.0), uAtlasGrid.z * 2.0);
  }
`;

const crowdFragmentShader = `
  precision mediump float;
  uniform sampler2D uAtlas;
  uniform vec3 uRimColor;
  uniform vec3 uHazeColor;
  uniform float uRimPulse;
  uniform float uCrowdLight;
  uniform vec3 uLaserColor;
  varying vec2 vUv;
  varying float vSafe;
  varying float vStep;
  varying float vHaze;
  varying float vRim;
  varying float vQuadY;
  varying float vLaser;
  void main() {
    float a = texture2D(uAtlas, vUv).a;
    if (a < 0.35) discard;
    float rim = 0.0;
    if (vHaze < 0.5) {
      float aL = texture2D(uAtlas, vUv - vec2(vStep, 0.0)).a;
      float aR = texture2D(uAtlas, vUv + vec2(vStep, 0.0)).a;
      float aD = texture2D(uAtlas, vUv - vec2(0.0, vStep)).a;
      float aU = texture2D(uAtlas, vUv + vec2(0.0, vStep)).a;
      vec2 g = vec2(aR - aL, aU - aD);
      float gLen = length(g);
      vec2 normal = gLen > 0.0001 ? -g / gLen : vec2(0.0, 1.0);
      // The key is the STAGE: above and ahead, so the rim sits on the top
      // contour, the far side of every head from the camera.
      rim = smoothstep(0.1, 0.9, gLen) * max(dot(normal, normalize(vec2(0.0, 1.0))), 0.0);
    }
    vec3 col = vec3(${BODY_LUMINANCE.toFixed(3)})
             + uRimColor * rim * vRim * ${RIM_INTENSITY.toFixed(2)} * uRimPulse * uCrowdLight * vSafe
             + uLaserColor * vLaser * (0.30 + rim * 1.4) * vSafe;
    col = mix(col, uHazeColor * vSafe, vHaze);
    col *= smoothstep(0.0, ${SPRITE_BOTTOM_FADE.toFixed(2)}, vQuadY);
    gl_FragColor = vec4(col, 1.0);
  }
`;

const phoneVertexShader = `
  precision highp float;
  attribute float iSeed;
  attribute float iPeriod;
  attribute float iHeight;
  attribute float iLeft;
  attribute float iCentre;
  attribute vec2 iAxis;
  uniform float uTime;
  uniform float uSway;
  uniform float uAllUp;
  uniform float uZone;
  uniform float uLitFraction;
  uniform float uFlickerAmp;
  uniform float uSizeScale;
  uniform float uPointSizeMax;
  varying float vLight;
  varying float vSafe;
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_REMAP}
  ${HAZE_CHUNK}
  ${WAVE_CHUNK}
  ${MOTION_CHUNK}
  void main() {
    float zone = uZone < 0.5 ? iLeft : (uZone < 1.5 ? 1.0 - iLeft : (uZone < 2.5 ? iCentre : 1.0));
    float lit = max(zone * step(iSeed, uLitFraction), uAllUp);
    if (lit < 0.5) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 1.0; vLight = 0.0; vSafe = 0.0; return; }
    float raise = max(waveRaise(uTime, iAxis), uAllUp);
    vec2 m = crowdMotion(uTime, iPeriod, iSeed, iHeight * ${SWAY_AMOUNT.toFixed(3)} * uSway, iHeight * ${BOB_AMOUNT.toFixed(3)});
    vec3 pos = position + vec3(m.x, m.y + raise * iHeight * ${WAVE_JUMP.toFixed(3)}, 0.0);
    vLight = 1.0 + uFlickerAmp * sin(uTime * (2.0 + 5.0 * iSeed) + iSeed * 6.2831);
    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mv;
    vSafe = safeRemap(safeZoneAttenuation(gl_Position));
    float depth = max(-mv.z, 0.001);
    vLight *= 1.0 - 0.6 * hazeAmount(depth);
    float px = mix(${PHONE_SIZE_PX[0].toFixed(1)}, ${PHONE_SIZE_PX[1].toFixed(1)}, iSeed);
    gl_PointSize = clamp(px * uSizeScale * ${(-Z_NEAR).toFixed(2)} / depth, 2.0, uPointSizeMax);
  }
`;

const phoneFragmentShader = `
  precision mediump float;
  uniform vec3 uPhoneColor;
  varying float vLight;
  varying float vSafe;
  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    float core = 1.0 - smoothstep(0.0, 0.45, d);
    float halo = 1.0 - smoothstep(0.3, 1.0, d);
    gl_FragColor = vec4(uPhoneColor * (core * 1.25 + halo * 0.30) * vLight * vSafe, 1.0);
  }
`;

// --- the facets: one mapped surface ---------------------------------------------
const facetVertexShader = `
  precision highp float;
  attribute vec2 aUvGlobal;   // on the whole structure's front plane
  attribute vec2 aUvLocal;    // within the facet, for its cells
  attribute vec4 aFacet;      // id, depth (0..8), distance to the diamond, cells per side
  attribute float aOn;        // the segment's visibility, faded on the CPU
  varying vec2 vUvG;
  varying vec2 vUvL;
  varying vec4 vFacet;
  varying float vOn;
  void main() {
    vUvG = aUvGlobal; vUvL = aUvLocal; vFacet = aFacet; vOn = aOn;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// MainStage's LED pipeline: content sampled at the CELL CENTRE, a mask for
// the gap between pixels without discard, per-cell jitter, seams.
const facetFragmentShader = `
  precision mediump float;
  uniform sampler2D uArtwork;
  uniform float uHasArt;
  uniform vec3 uColor0;
  uniform vec3 uColor1;
  uniform vec3 uColor2;
  uniform float uMode;
  uniform float uModePrev;
  uniform float uModeMix;
  uniform float uDrop;        // 0 none, 1 diamond artwork + white, 2 inverted
  uniform float uPulseT;      // beats, continuous
  uniform float uTime;
  uniform float uBrightness;
  uniform float uNoise;       // the beat's video noise, 0..1
  varying vec2 vUvG;
  varying vec2 vUvL;
  varying vec4 vFacet;
  varying float vOn;
  ${HASH_CHUNK}
  ${SAFE_FRAGMENT_CHUNK}

  vec3 capLuminance(vec3 col, float cap) {
    float lum = dot(col, vec3(0.2125, 0.7154, 0.0721));
    return lum > cap ? col * (cap / lum) : col;
  }

  vec3 content(float mode, vec2 uvG, vec2 cellId, float depth, float dist) {
    if (mode < 0.5) {
      return mix(uColor1 * 0.6, texture2D(uArtwork, uvG).rgb, uHasArt);
    } else if (mode < 1.5) {
      // SOLID, darker with depth: the recessed facets sit back.
      return uColor0 * (1.0 - 0.55 * depth / ${WING_DEPTH_MAX.toFixed(1)});
    } else if (mode < 2.5) {
      float s = fract((uvG.x + uvG.y) * 5.0 - uTime * 0.35);
      float band = smoothstep(0.42, 0.5, s) * smoothstep(0.58, 0.5, s);
      return mix(uColor1 * 0.25, uColor0, band) + uColor2 * step(0.96, s) * 0.8;
    } else if (mode < 3.5) {
      // PULSE: a ring leaves the diamond on every beat and crosses the crystal.
      float front = fract(uPulseT) * 40.0;
      float d = dist - front;
      float ring = exp(-d * d * 0.03) + 0.35 * exp(-(dist - front + 40.0) * (dist - front + 40.0) * 0.03);
      return uColor0 * (0.12 + ring);
    }
    // NOISE: video snow.
    float n = hash21(cellId + floor(uTime * 24.0));
    return vec3(n * n) * 0.9;
  }

  void main() {
    vec2 c = vUvL * vFacet.w;
    vec2 f = fract(c);
    vec2 cellId = floor(c) + vFacet.x * 97.0;
    float m = smoothstep(0.0, 0.14, f.x) * smoothstep(1.0, 0.86, f.x)
            * smoothstep(0.0, 0.14, f.y) * smoothstep(1.0, 0.86, f.y);
    float jitter = 0.86 + 0.14 * hash21(mod(cellId, 512.0));
    float seam = (mod(floor(c.x), 8.0) < 0.5 || mod(floor(c.y), 8.0) < 0.5) ? 0.78 : 1.0;
    // The content is sampled at the cell's centre, in GLOBAL uv, so a picture
    // is quantised to the pitch rather than smoothly interpolated under a grid.
    vec2 cellUvG = vUvG + ((floor(c) + 0.5) / vFacet.w - vUvL) * 0.0;
    float depth = vFacet.y, dist = vFacet.z;
    vec3 col = mix(content(uModePrev, cellUvG, cellId, depth, dist),
                   content(uMode, cellUvG, cellId, depth, dist), uModeMix);
    // The drop: the diamond alone carries the cover and everything else goes
    // white; then the inverse.
    if (uDrop > 0.5) {
      float isDiamond = step(vFacet.x, 0.5);
      vec3 art = mix(uColor0, texture2D(uArtwork, cellUvG).rgb, uHasArt);
      float artOn = uDrop < 1.5 ? isDiamond : 1.0 - isDiamond;
      // 0.62 and not 1.0: forty facets at full white through the bloom took
      // the whole frame to a mean of 130 - a wash, not a flash. The strobe on
      // the bars and the lyres in white carry the hit.
      col = mix(vec3(0.62), art, artOn);
    }
    col = mix(col, vec3(0.9), uNoise * hash21(cellId + floor(uTime * 30.0)) * 0.8);
    col *= m * jitter * seam * uBrightness * vOn;
    // The relief: a facet eight units back is a third darker than one at
    // the front.
    col *= 1.0 - ${FACET_DEPTH_DARKEN.toFixed(2)} * clamp(depth / ${WING_DEPTH_MAX.toFixed(1)}, 0.0, 1.0);
    col = capLuminance(col, ${FACET_PEAK.toFixed(2)}) * safeHere();
    // A facet that is off is a dark panel, not a hole.
    col += vec3(0.010, 0.011, 0.016) * m;
    gl_FragColor = vec4(col, 1.0);
  }
`;

// --- the pixel bars -------------------------------------------------------------------
const barVertexShader = `
  precision highp float;
  attribute float aLevel;
  attribute float aSeed;
  uniform float uStrobe;
  uniform float uSizeScale;
  uniform float uPointSizeMax;
  varying float vLevel;
  varying float vSafe;
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_REMAP}
  void main() {
    vLevel = max(aLevel, uStrobe);
    vec4 mv = modelViewMatrix * vec4(position, 1.0);
    gl_Position = projectionMatrix * mv;
    vSafe = safeRemap(safeZoneAttenuation(gl_Position));
    float depth = max(-mv.z, 0.001);
    gl_PointSize = clamp(${BAR_PIXEL_PX.toFixed(1)} * uSizeScale * ${(-STAGE_Z).toFixed(1)} / depth * (0.8 + 0.6 * vLevel), 1.5, uPointSizeMax);
  }
`;

const barFragmentShader = `
  precision mediump float;
  uniform vec3 uBarColor;
  varying float vLevel;
  varying float vSafe;
  void main() {
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    float core = 1.0 - smoothstep(0.0, 0.6, d);
    float halo = 1.0 - smoothstep(0.2, 1.0, d);
    // Dim pixels keep the colour; a full pixel goes near-white, which is what
    // the bloom picks up on a chase's head.
    vec3 col = mix(uBarColor * 0.10, mix(uBarColor, vec3(1.0), 0.70), vLevel);
    gl_FragColor = vec4(col * (core * 1.5 + halo * 0.35) * vSafe, 1.0);
  }
`;

// --- the lyres: BeamWall's choreography, base axis to the sky -------------------
const LYRE_CHOREO_CHUNK = `
  uniform float uCols;
  uniform float uRows;
  uniform float uPat;
  uniform float uPatPrev;
  uniform float uPatMix;
  uniform float uStep;
  uniform float uStepMix;
  uniform vec3 uConverge;
  uniform vec3 uConvergePrev;
  uniform float uBurst;
  uniform float uSpread;

  float bwHash(float n) { return fract(sin(mod(n, 6283.1853)) * 43758.5453); }

  // Base direction is +y: straight up, out of the top of the frame. yaw
  // leans sideways, pitch leans toward the audience.
  vec3 lyreAngles(float yaw, float pitch) {
    float cp = cos(pitch);
    return normalize(vec3(sin(yaw) * cp, cos(yaw) * cp, sin(pitch)));
  }

  // cx: -1..1 across the structure, cy: -1..1 up it, both from where the
  // head is MOUNTED - the heads sit on the wings, the columns and the
  // octagon, not on a rail of their own.
  vec3 lyrePatDir(float pat, float cx, float cy, float id, float s, vec3 o, out float gain) {
    gain = 1.0;
    float yaw = 0.0, pitch = 0.0;
    float bm = uSpread * (1.0 + uBurst * 1.6);
    if (pat < 0.5) {
      // FAN: the row opens as one shutter, the lean along the fixture's own
      // radius from the centre of the row.
      float amt = 0.35 + 0.65 * abs(sin(s * 1.0471975512));
      float a = abs(cx) * 0.55 * amt * bm;
      return lyreAngles(sign(cx) * a, 0.08 * bm);
    } else if (pat < 1.5) {
      // WAVE, over the crowd: the sine travels along the row while every
      // beam rakes forward and down.
      yaw = 0.40 * sin(cx * 1.1 + cy * 0.35 + s * 0.7853981634);
      pitch = 1.85 + cy * 0.10;
      return lyreAngles(yaw, pitch);
    } else if (pat < 2.5) {
      float d = length(vec2(cx, cy));
      float a = sin(d * 3.4 - s * 0.7853981634);
      yaw = cx * (0.20 + 0.42 * a);
      pitch = 0.10 + 0.20 * a;
    } else if (pat < 3.5) {
      vec3 tgt = (s < uStep - 0.5) ? uConvergePrev : uConverge;
      vec3 cd = normalize(tgt - o);
      vec3 od = lyreAngles(cx * 0.6, 0.25);
      return normalize(mix(cd, od, uBurst));
    } else if (pat < 4.5) {
      // RAIN: every fixture on the same vector, DOWN onto the crowd - past
      // the horizontal (pi/2) the beams descend; the angle steps a little
      // every notch.
      float ph = 0.5 + 0.5 * sin(s * 0.7853981634);
      pitch = 1.80 + 0.35 * ph;
      yaw = 0.0;
      return lyreAngles(yaw, pitch);
    } else if (pat < 5.5) {
      float head = mod(s, uCols);
      float d = abs(id - head);
      d = min(d, uCols - d);
      gain = d < 0.5 ? 1.0 : (d < 1.5 ? 0.35 : 0.06);
      yaw = cx * 0.10;
      pitch = 0.12;
    } else {
      float n = mod(id * 7.13 + s * 31.7, 4096.0);
      yaw = (bwHash(n) * 2.0 - 1.0) * 0.72;
      pitch = (bwHash(n + 11.3) * 2.0 - 1.0) * 0.30 + 0.12;
    }
    return lyreAngles(yaw * bm, pitch * bm);
  }

  vec3 lyreStepDir(float pat, float cx, float cy, float id, vec3 o, out float gain) {
    float gA, gB;
    vec3 dA = lyrePatDir(pat, cx, cy, id, uStep - 1.0, o, gA);
    vec3 dB = lyrePatDir(pat, cx, cy, id, uStep, o, gB);
    gain = mix(gA, gB, uStepMix);
    vec3 m = mix(dA, dB, uStepMix);
    float ml = length(m);
    return ml > 0.0001 ? m / ml : dB;
  }

  vec3 lyreDir(float cx, float cy, float id, vec3 o, out float gain) {
    float gB;
    vec3 dB = lyreStepDir(uPat, cx, cy, id, o, gB);
    if (uPatMix > 0.999) { gain = gB; return dB; }
    float gA;
    vec3 dA = lyreStepDir(uPatPrev, cx, cy, id, o, gA);
    gain = mix(gA, gB, uPatMix);
    vec3 m = mix(dA, dB, uPatMix);
    float ml = length(m);
    return ml > 0.0001 ? m / ml : dB;
  }
`;

const LYRE_BILLBOARD_CHUNK = `
  uniform float uPixelScale;
  vec3 bwPerp(vec3 n) {
    vec3 a = abs(n.y) < 0.9 ? vec3(0.0, 1.0, 0.0) : vec3(1.0, 0.0, 0.0);
    return normalize(cross(n, a));
  }
  vec3 bwBillboard(vec3 along, vec3 dir, float t, float sideSign, out float guard) {
    vec3 toCam = cameraPosition - along;
    float camDist = length(toCam);
    toCam = camDist > 0.0001 ? toCam / camDist : vec3(0.0, 0.0, 1.0);
    float align = abs(dot(dir, toCam));
    guard = mix(0.30, 1.0, 1.0 - smoothstep(0.90, 0.999, align));
    vec3 side = cross(dir, toCam);
    float sideLen = length(side);
    side = sideLen > 0.0001 ? side / sideLen : bwPerp(dir);
    vec4 mv = modelViewMatrix * vec4(along, 1.0);
    float viewDepth = max(-mv.z, 0.001);
    float worldPerPx = viewDepth / uPixelScale;
    float halfWidth = mix(${LYRE_NEAR_PX.toFixed(1)}, ${LYRE_FAR_PX.toFixed(1)}, t) * 0.5 * worldPerPx;
    return along + side * (halfWidth * sideSign);
  }
`;

const lyreVertexShader = `
  precision highp float;
  attribute vec3 iPos;         // where the head is mounted on the structure
  attribute float iCol;        // its rank left to right
  attribute vec2 iAxis;        // cx, cy
  uniform float uMasterGain;
  uniform vec3 uColorA;
  uniform float uWhite;
  varying float vSide;
  varying float vDist;
  varying vec3 vColor;
  varying float vGain;
  varying float vSafeAtt;
  ${LYRE_CHOREO_CHUNK}
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_REMAP}
  ${LYRE_BILLBOARD_CHUNK}
  void main() {
    float id = iCol;
    float gain;
    vec3 dir = lyreDir(iAxis.x, iAxis.y, id, iPos, gain);
    vec3 origin = iPos + dir * 0.3;
    float t = position.y;
    vec3 along = origin + dir * (t * ${LYRE_BEAM_LEN.toFixed(1)});
    float guard;
    vec3 worldPos = bwBillboard(along, dir, t, position.x, guard);
    vSide = position.x;
    vDist = t * ${LYRE_BEAM_LEN.toFixed(1)};
    vColor = mix(uColorA, vec3(1.0), uWhite);
    vGain = uMasterGain * gain * guard;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
    vSafeAtt = safeRemap(safeZoneAttenuation(gl_Position));
  }
`;

const lyreFragmentShader = `
  precision mediump float;
  uniform float uPulse;
  varying float vSide;
  varying float vDist;
  varying vec3 vColor;
  varying float vGain;
  varying float vSafeAtt;
  ${laserClampChunk(LYRE_PEAK)}
  void main() {
    float u = abs(vSide);
    float core = 1.0 - smoothstep(0.0, 0.40, u);
    float e = max(1.0 - u, 0.0);
    float halo = e * sqrt(e);
    float atten = exp(-vDist * ${LYRE_EXT.toFixed(3)})
                * (1.0 - smoothstep(${(LYRE_BEAM_LEN * 0.55).toFixed(1)}, ${LYRE_BEAM_LEN.toFixed(1)}, vDist));
    vec3 hot = mix(vColor, vec3(1.0), ${LYRE_HOT_MIX.toFixed(2)});
    vec3 col = vColor * halo * ${LYRE_HALO_W.toFixed(3)} + hot * core * ${LYRE_CORE_W.toFixed(3)};
    col *= atten * uPulse * vGain * vSafeAtt;
    gl_FragColor = vec4(clampLuminance(col), 1.0);
  }
`;

// --- the lasers: LaserArray's primitives, fans and scans over the crowd ----------
const laserVertexShader = `
  precision highp float;
  attribute float iEmitter;
  attribute float iIndex;
  attribute float iSeed;
  uniform vec3 uEmitter[${LASER_EMITTERS}];
  uniform float uLaserMode;     // 0 fan, 1 scan
  uniform float uLaserPhase;    // 0..1 over a bar
  uniform float uLaserSpread;
  uniform float uMasterGain;
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  varying float vSide;
  varying float vLen;
  varying vec3 vColor;
  varying float vSeed;
  varying float vGain;
  varying float vSafeAtt;
  ${SAFE_ZONE_VERTEX_CHUNK}
  ${SAFE_REMAP}
  ${laserBillboardChunk({ halfWidthNear: LASER_HALF_NEAR, halfWidthFar: LASER_HALF_FAR })}

  vec3 rotY(vec3 v, float a) {
    float c = cos(a), s = sin(a);
    return vec3(v.x * c + v.z * s, v.y, -v.x * s + v.z * c);
  }

  void main() {
    vec3 origin = uEmitter[int(iEmitter)];
    // DOWN over the crowd, toward the camera. FAN: a flat fan at one pitch,
    // spread sideways; SCAN: a thin sheet whose pitch sweeps from shallow to
    // steep and back once a bar - at the steep end it nearly reaches the
    // camera. The side emitters lean toward the middle.
    float lean = (iEmitter < 0.5) ? 0.0 : (iEmitter < 1.5 ? -0.30 : 0.30);
    float frac = (iIndex / ${(LASER_PER_EMITTER - 1).toFixed(1)}) - 0.5;
    float ph = uLaserPhase * 6.2831853;
    float pitch, yaw;
    if (uLaserMode < 0.5) {
      pitch = ${LASER_PITCH_FAN.toFixed(4)};
      yaw = frac * uLaserSpread * (0.6 + 0.4 * sin(ph));
    } else {
      pitch = mix(${LASER_PITCH_SCAN[0].toFixed(4)}, ${LASER_PITCH_SCAN[1].toFixed(4)}, 0.5 - 0.5 * cos(ph));
      yaw = frac * 0.10;
    }
    vec3 base = vec3(0.0, -sin(pitch), cos(pitch));
    vec3 dir = rotY(base, yaw + lean);
    float t = position.y;
    vec3 along = origin + dir * (t * ${LASER_LEN.toFixed(1)});
    float guard;
    vec3 worldPos = laserBillboard(along, dir, t, position.x, guard);
    vSide = position.x;
    vLen = t;
    vSeed = iSeed;
    vColor = mod(iIndex, 2.0) < 0.5 ? uColorA : uColorB;
    vGain = uMasterGain * guard;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(worldPos, 1.0);
    vSafeAtt = safeRemap(safeZoneAttenuation(gl_Position));
  }
`;

const laserFragmentShader = laserBeamFragmentShader({
  hotMix: LASER_HOT_MIX, peakLuminance: LASER_PEAK, fadeStart: '0.75', fadeEnd: '1.05'
});

// --- the CO2 cannons -----------------------------------------------------------------
const co2VertexShader = `
  precision highp float;
  attribute float aSeed;
  attribute float aCannon;
  uniform float uTime;
  uniform float uStart;
  uniform vec2 uCannonX;
  uniform float uSizeScale;
  uniform float uPointSizeMax;
  varying float vAlpha;
  void main() {
    float age = uTime - uStart - aSeed * ${CO2_SECONDS.toFixed(2)};
    float life = 1.1;
    if (uStart < 0.0 || age < 0.0 || age > life) { gl_Position = vec4(2.0, 2.0, 2.0, 1.0); gl_PointSize = 1.0; vAlpha = 0.0; return; }
    float u = age / life;
    // A jet: fast and narrow at the mouth, slowing and widening as it rises.
    float rise = 14.0 * (1.0 - exp(-age * 3.5));
    float x = (aCannon < 0.5 ? uCannonX.x : uCannonX.y) + (aSeed - 0.5) * (0.4 + 2.5 * u);
    vec3 pos = vec3(x, ${DECK_Y.toFixed(1)} + rise, ${(STAGE_Z + 0.5).toFixed(1)} + (fract(aSeed * 7.0) - 0.5) * 1.5);
    vAlpha = (1.0 - u) * (1.0 - u) * 0.55;
    vec4 mv = modelViewMatrix * vec4(pos, 1.0);
    gl_Position = projectionMatrix * mv;
    gl_PointSize = clamp((28.0 + 60.0 * u) * uSizeScale * ${(-STAGE_Z).toFixed(1)} / max(-mv.z, 0.001), 2.0, uPointSizeMax);
  }
`;

const co2FragmentShader = `
  precision mediump float;
  varying float vAlpha;
  void main() {
    if (vAlpha < 0.005) discard;
    vec2 uv = gl_PointCoord - vec2(0.5);
    float d = length(uv) * 2.0;
    float a = exp(-d * d * 3.0) * vAlpha;
    gl_FragColor = vec4(vec3(1.0), a);
  }
`;

// --- the city and the bay --------------------------------------------------------------
const cityVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Nightdrive's skyline, one layer, over a bay that mirrors it in streaks.
const cityFragmentShader = `
  precision mediump float;
  uniform vec3 uSkyTop;
  uniform vec3 uSkyHorizon;
  uniform vec3 uHazeColor;
  uniform vec3 uPal0;
  uniform vec3 uPal1;
  uniform vec3 uPal2;
  uniform vec3 uStageGlow;    // what the structure throws on the water
  uniform float uHorizon;     // in plane uv
  uniform float uSeed;
  uniform float uTime;
  uniform float uHigh;
  uniform float uBarPulse;
  uniform float uProcedural;  // 1: the procedural buildings (no plate), 0: sky and water only
  varying vec2 vUv;
  ${HASH_CHUNK}
  ${SAFE_FRAGMENT_CHUNK}

  // One layer of skyline. cx is the city coordinate; y is height above the
  // waterline in the same units. Returns coverage in .x, window light in .y,
  // and a sign's colour index in .z (-1: none).
  vec3 layer(float cx, float y, float seed, float scale, float lit) {
    float slot = floor(cx);
    float f = fract(cx);
    float w = 0.55 + 0.25 * h11(slot * 1.7 + seed);
    float h = (0.45 + 0.55 * h11(slot * 3.1 + seed + 11.0)) * scale;
    if (f > w) return vec3(0.0, 0.0, -1.0);
    if (y > h || y < 0.0) return vec3(0.0, 0.0, -1.0);
    vec2 cell = vec2(floor((f / w) * 6.0), floor((y / h) * 16.0));
    float r = hash21(mod(cell + vec2(slot * 13.0, seed * 7.0), 256.0));
    float on = step(1.0 - lit, r);
    // A few floors entirely dark: the grid is irregular, like a real one.
    on *= step(0.22, h11(cell.y * 7.7 + slot * 3.3 + seed));
    float fl = step(0.965, hash21(mod(cell.yx + seed, 256.0)));
    on *= mix(1.0, 0.35 + 0.65 * abs(sin(uTime * (2.0 + 6.0 * r) + r * 30.0)), fl);
    vec2 inCell = fract(vec2((f / w) * 6.0, (y / h) * 16.0));
    float shape = step(0.30, inCell.x) * step(inCell.x, 0.70) * step(0.34, inCell.y) * step(inCell.y, 0.66);
    float bright = 0.12 + 1.5 * r * r;
    // A sign: one building in five carries a coloured band near its top,
    // blinking on its own clock.
    float hasSign = step(0.8, h11(slot * 5.3 + seed + 3.0));
    float signBand = step(0.80 * h, y) * step(y, 0.88 * h) * step(0.15, f / w) * step(f / w, 0.85);
    float blink = 0.6 + 0.4 * step(0.5, fract(uTime * (0.4 + 0.5 * h11(slot + 2.0)) + h11(slot)));
    float sign = hasSign * signBand * blink;
    // One tower in three carries a vertical LED band that pulses softly on
    // the bars - very faint, a detail for the eye that lingers.
    float hasBand = step(0.66, h11(slot * 4.1 + seed + 9.0));
    float band = hasBand * step(abs(f / w - 0.5), 0.05) * step(y, h * 0.96);
    float bandLight = band * (0.10 + 0.12 * uBarPulse);
    return vec3(1.0, on * shape * bright + bandLight, sign > 0.5 ? mod(slot, 3.0) : -1.0);
  }

  vec3 skyline(float sx, float y, out float cover) {
    cover = 0.0;
    // Two or three towers at each end of the frame, tall and apart; nothing
    // behind the structure.
    // sx here is the PLANE's coordinate, and the plane is 1.54 frame widths
    // wide: the frame's edge sits at |sx| = 1.16 and the wing tips at 0.86.
    // (At 1.28 the towers were off screen and the picture had none.)
    // A far, dim layer right across - it shows wherever the stage and the
    // near towers leave a gap - and the near towers, four or five a side
    // beyond the wings' tips, at 70% of the earlier scale.
    vec3 far = layer(sx * 16.0 + uSeed * 3.1, y, uSeed + 41.0, 0.16, 0.45 + 0.2 * uHigh);
    vec3 col = vec3(0.0);
    if (far.x > 0.5) {
      cover = 1.0;
      col = vec3(0.014, 0.016, 0.028) + vec3(0.95, 0.85, 0.65) * far.y * 0.22;
    }
    if (abs(sx) < 0.78) return col;
    float cx = sx * 9.0 + uSeed * 7.3;
    vec3 b = layer(cx, y, uSeed, 0.32, 0.55 + 0.25 * uHigh);
    if (b.x < 0.5) return col;
    cover = 1.0;
    vec3 pal = b.z < 0.5 ? uPal0 : (b.z < 1.5 ? uPal1 : uPal2);
    vec3 blk = vec3(0.010, 0.012, 0.022);
    vec3 win = vec3(0.95, 0.85, 0.65) * b.y * 0.42;
    vec3 sgn = b.z >= 0.0 ? pal * 0.7 : vec3(0.0);
    return blk + win + sgn;
  }

  void main() {
    vec2 p = vUv;
    float y = p.y - uHorizon;
    float sx = (p.x - 0.5) * 2.0 * uAspect;
    // Night sky, warmed at the horizon by the city.
    float sky = clamp(y / (1.0 - uHorizon), 0.0, 1.0);
    vec3 col = mix(uSkyHorizon, uSkyTop, pow(sky, 0.6));
    float halo = exp(-max(y, 0.0) * 5.2);
    col += uHazeColor * halo * 0.7;

    if (y > 0.0) {
      if (uProcedural > 0.5) {
        float cover;
        vec3 c = skyline(sx, y, cover);
        col = mix(col, c, cover);
      }
    } else if (uProcedural < 0.5) {
      // the plate draws its own water; this only shows past its edges
      col = vec3(0.008, 0.010, 0.018) + uStageGlow * exp(-abs(sx) * 0.35) * 0.3;
    } else {
      // THE BAY. Dark water; the skyline's lights and the stage's own glow
      // come down it as streaks, stretched and torn by the swell.
      float depthW = -y;
      float ripple = hash21(vec2(floor(p.x * 900.0), floor(uTime * 6.0)));
      float sway = (ripple - 0.5) * 0.012 * (0.3 + depthW * 6.0);
      float cover;
      vec3 c = skyline(sx + sway, depthW * (1.0 + 0.6 * depthW * 8.0), cover);
      float fade = exp(-depthW * 9.0);
      col = mix(vec3(0.008, 0.010, 0.018), c * 0.55, cover * fade);
      // A vertical grain: streaks, not a mirror.
      float streak = 0.75 + 0.25 * hash21(vec2(floor(p.x * 700.0), 1.0));
      col *= streak;
      col += uStageGlow * exp(-abs(sx) * 0.35) * fade * 0.5 * streak;
    }
    col *= safeHere();
    gl_FragColor = vec4(col, 1.0);
  }
`;

// --- the plate --------------------------------------------------------------------------
const plateVertexShader = `
  precision highp float;
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

// Per pixel: colour = off + (on - off) * lit(tower), the photographs decoded
// to linear by the GPU, the sky's alpha dividing the premultiplied colour
// back. The haze takes PLATE_CONTRAST of the contrast toward the scene's
// ambient, denser at the ground; the scene's light (beams, strobes, pyro)
// washes the low facades; a tint follows the sky and the palette. Below the
// ground line: the bay, a streaked mirror of the image above.
const plateFragmentShader = `
  precision highp float;
  uniform sampler2D uOn;
  uniform sampler2D uOff;
  uniform highp sampler2D uIds;
  uniform sampler2D uLit;       // one texel per tower, R = lit / CITY_LIT_MAX
  uniform float uTowers;
  uniform float uStatic;        // 1: no off/ids - the lit picture as is
  uniform float uGround;        // v of the ground line in the image
  uniform float uVMin;          // the quad's v at its bottom (the water)
  uniform vec3 uHazeColor;
  uniform vec3 uTint;
  uniform vec3 uSceneLight;
  uniform vec3 uStageGlow;
  uniform vec2 uTexel;
  uniform float uTime;
  uniform float uMaskDebug;
  uniform float uContrastKeep;
  varying vec2 vUv;
  ${HASH_CHUNK}
  ${SAFE_FRAGMENT_CHUNK}

  vec4 plateAt(vec2 uv) {
    // a 1 px blur of depth: four taps on the lit picture
    vec2 o = uTexel * ${PLATE_BLUR_PX.toFixed(2)};
    vec4 on = 0.25 * (texture2D(uOn, uv + vec2(o.x, 0.0)) + texture2D(uOn, uv - vec2(o.x, 0.0)) + texture2D(uOn, uv + vec2(0.0, o.y)) + texture2D(uOn, uv - vec2(0.0, o.y)));
    float a = on.a;
    vec3 col = on.rgb / max(a, 0.02);
    if (uStatic < 0.5) {
      vec4 off = texture2D(uOff, uv);
      vec3 offc = off.rgb / max(off.a, 0.02);
      vec3 diff = max(col - offc, 0.0);
      highp vec4 idc = texture2D(uIds, uv);
      float id = floor(idc.r * 255.0 + 0.5) * 256.0 + floor(idc.g * 255.0 + 0.5);
      float lit = id > 0.5 ? texture2D(uLit, vec2((id - 0.5) / uTowers, 0.5)).r * ${CITY_LIT_MAX.toFixed(1)} : 1.0;
      col = offc + diff * lit;
    }
    return vec4(col, a);
  }

  void main() {
    // v runs from uVMin (water) to 1 (top of the image); the image's own v is [0, 1]
    float v = uVMin + vUv.y * (1.0 - uVMin);
    vec3 col; float a;
    float h = clamp((v - uGround) / (1.0 - uGround) * 1.6, 0.0, 1.0);   // height above the ground, 1 at the tallest crest
    if (v >= uGround) {
      vec4 p = plateAt(vec2(vUv.x, v));
      col = p.rgb; a = p.a;
    } else {
      // THE BAY: the image mirrored at the ground, torn by the swell, fading
      // with the distance below the line
      float d = (uGround - v) / max(uGround - uVMin, 0.001);
      float ripple = hash21(vec2(floor(vUv.x * 900.0), floor(uTime * 6.0)));
      float sway = (ripple - 0.5) * 0.010 * (0.3 + d * 4.0);
      vec4 p = plateAt(vec2(vUv.x + sway, uGround + (uGround - v) * (1.0 + 0.5 * d)));
      float fade = exp(-d * 4.5);
      float streak = 0.75 + 0.25 * hash21(vec2(floor(vUv.x * 700.0), 1.0));
      col = mix(vec3(0.008, 0.010, 0.018), p.rgb * 0.45, p.a * fade) * streak;
      col += uStageGlow * exp(-abs(vUv.x - 0.5) * 2.5) * fade * 0.4 * streak;
      a = 1.0;
      h = 0.0;
    }
    if (uMaskDebug > 0.5 && a < 0.5) { gl_FragColor = vec4(1.0, 0.0, 1.0, 1.0); return; }
    if (a < 0.5) discard;
    // atmospheric perspective: contrast toward the ambient, denser low
    float haze = ${PLATE_CONTRAST.toFixed(2)} * mix(1.15, 0.75, h) * uContrastKeep;
    col = mix(col, uHazeColor * 1.4, clamp(haze, 0.0, 0.5));
    // the scene's light on the low facades
    col += uSceneLight * ${PLATE_SCENE_LIGHT.toFixed(2)} * (1.0 - h);
    // calibration toward the sky and the palette
    col *= mix(vec3(1.0), uTint, ${PLATE_TINT.toFixed(2)});
    col *= safeHere();
    gl_FragColor = vec4(col, 1.0);
  }
`;

const hazeLayerVertexShader = `
  precision mediump float;
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
`;
// The haze between the scene and the city: denser at the ground line, which
// it hides, thinning upward. Alpha-blended, no depth write.
const hazeLayerFragmentShader = `
  precision mediump float;
  uniform vec3 uHazeColor;
  uniform float uGroundV;      // v of the floor line on this quad
  varying vec2 vUv;
  void main() {
    float up = clamp((vUv.y - uGroundV) / max(1.0 - uGroundV, 0.01), 0.0, 1.0);
    float below = clamp((uGroundV - vUv.y) / max(uGroundV, 0.01), 0.0, 1.0);
    float a = mix(${HAZE_LAYER_BOTTOM.toFixed(2)}, ${HAZE_LAYER_TOP.toFixed(2)}, pow(up, 0.6));
    a *= 1.0 - 0.6 * below;
    gl_FragColor = vec4(uHazeColor * 1.5, a);
  }
`;

// =============================================================================

export class BayfrontVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
    this._atlas = options && options.crowdAtlas !== undefined ? options.crowdAtlas : getCrowdAtlas();
    const seg = urlParam('bayfrontsegment');
    this._forceSegment = SEGMENTS.indexOf(seg) >= 0 ? seg : null;
    this._onlyLayer = urlParam('bayfrontlayer');
    const content = urlParam('bayfrontcontent');
    this._forceContent = content != null && CONTENT[content] != null ? CONTENT[content] : null;
    this._forceChase = urlParam('bayfrontchase') === '1';
    this._forceDrop = urlParam('bayfrontdrop') === '1';
    this._noPlate = urlParam('bayskyline') === '0';
    this._plateMaskDebug = urlParam('bayskylinemask') === '1';
    const fl = parseFloat(urlParam('baycitylight'));
    this._forceCityLight = Number.isFinite(fl) ? fl : null;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    const pitch = THREE.MathUtils.degToRad(CAMERA_PITCH_DEG);
    camera.lookAt(0, CAMERA_POS[1] + 40 * Math.tan(pitch), -40);
    camera.updateMatrixWorld();
    camera.updateProjectionMatrix();
    this._previousFog = scene.fog;
    // The haze over the crowd the sheets are seen in. Only the built-in
    // materials read this; every ShaderMaterial here applies its own.
    scene.fog = new THREE.FogExp2(0x000000, 0.04);

    const layout = TIER_LAYOUT[this._tier];
    this._layout = layout;
    this._time = 0;
    this._beatSeconds = 0.5;
    this._beatCount = 0;
    this._lastBeatPhase = null;
    this._lastBarPhase = null;
    this._lastPhrase = null;
    this._freeBeatAt = 0;
    this._zoneStep = 3;
    this._waves = [];
    for (let i = 0; i < WAVE_SLOTS; i++) this._waves.push(new THREE.Vector4(-1, 0, 0, 0));
    this._waveTurn = 0;
    this._pendingDropMs = null;
    this._lastDropAt = -1e9;
    this._firstDropAt = -1;
    this._dropUntil = -1;
    this._dropPhase = 0;
    this._segment = 'intro';
    this._segmentSince = 0;
    this._lastMode = null;
    this._content = CONTENT_CYCLE[0];
    this._contentPrev = CONTENT_CYCLE[0];
    this._contentMix = 1;
    this._contentTurn = 0;
    this._noise = 0;
    this._pulseT = 0;
    this._barPattern = 0;
    this._strobeTemp = 0;
    this._strobeUntil = -1;
    this._lyreStep = 0;
    this._lyreStepAt = 0;
    this._lyrePat = LYRE_PAT.rain;
    this._lyrePatPrev = LYRE_PAT.rain;
    this._lyrePatSince = -10;
    this._lyreTurn = 0;
    this._laserMode = 0;
    this._lastPaletteRef = null;
    this._lastArtworkUrl = null;
    this._artwork = null;

    this._buildCity();
    this._plate = null;
    this._disposed = false;
    this._cityLit = null;
    this._cityDropAt = -1e9;
    this._cityWaveAt = -1e9;
    this._buildSince = -1;
    this._pyroFlash = 0;
    if (!this._noPlate) {
      const self = this;
      preloadBayfrontSkyline().then(function () { return loadBayfrontSkyline(self._tier < 2); }).then(function (assets) {
        if (self._disposed || !assets) return;
        self._buildPlate(assets);
      });
    }
    this._buildDeck();
    this._buildBooth();
    this._buildStructure(layout);
    if (layout.bars) this._buildBars();
    this._buildLyres(layout.lyres);
    if (layout.lasers) this._buildLasers();
    if (layout.pyro) this._buildPyro();
    if (layout.co2) this._buildCo2();
    if (this._atlas) this._buildCrowd(layout);
    this._applyPalette(null);
    this._applyLayerFilter();
  }

  // ---------------------------------------------------------------------------
  // The structure: facets on a shared vertex graph
  // ---------------------------------------------------------------------------
  // Every facet is a polygon over vertex IDS; vertices are deduplicated by
  // position, which is what makes the graph shared. A facet also carries its
  // priority, so the lower tiers keep the ones that matter.
  _buildStructure(layout) {
    const verts = [];
    const vmap = new Map();
    const V = (x, y, z) => {
      const key = x.toFixed(2) + ',' + y.toFixed(2) + ',' + z.toFixed(2);
      let id = vmap.get(key);
      if (id == null) { id = verts.length; verts.push([x, y, z]); vmap.set(key, id); }
      return id;
    };
    const facets = [];   // { ids: [...], depth, prio }
    const Z = STAGE_Z, cy = DIAMOND_CENTER_Y;

    // The diamond, leaning toward the audience.
    facets.push({ ids: [V(0, cy + DIAMOND_HALF_H, Z + DIAMOND_TILT), V(DIAMOND_HALF_W, cy, Z), V(0, cy - DIAMOND_HALF_H, Z - DIAMOND_TILT), V(-DIAMOND_HALF_W, cy, Z)], depth: 0, prio: 0 });
    // The octagon: eight trapezoids sharing their vertices.
    for (let i = 0; i < 8; i++) {
      const a0 = (i / 8) * Math.PI * 2 + Math.PI / 8, a1 = ((i + 1) / 8) * Math.PI * 2 + Math.PI / 8;
      const z = Z - OCTAGON_DEPTH;
      facets.push({ ids: [
        V(Math.cos(a0) * OCTAGON_R_IN, cy + Math.sin(a0) * OCTAGON_R_IN, z),
        V(Math.cos(a0) * OCTAGON_R_OUT, cy + Math.sin(a0) * OCTAGON_R_OUT, z),
        V(Math.cos(a1) * OCTAGON_R_OUT, cy + Math.sin(a1) * OCTAGON_R_OUT, z),
        V(Math.cos(a1) * OCTAGON_R_IN, cy + Math.sin(a1) * OCTAGON_R_IN, z)
      ], depth: OCTAGON_DEPTH, prio: 1 });
    }
    // The wings: one strip of facets a side between an inner and an outer
    // polyline, opening outward and upward, the depth stepping along it.
    for (const sx of [-1, 1]) {
      const inner = [], outer = [];
      for (let i = 0; i <= WING_FACETS; i++) {
        const u = i / WING_FACETS;
        const depth = WING_DEPTH_MAX * u;
        inner.push(V(sx * (15.5 + 11.0 * u), SOCLE_TOP + 27.0 * u, Z - 0.5 - depth));
        outer.push(V(sx * (24.0 + 17.0 * u), SOCLE_TOP + 29.0 * u, Z - 0.5 - depth - 1.0));
      }
      for (let i = 0; i < WING_FACETS; i++) {
        // Quads and triangles alternate, so the strip reads as cut crystal.
        const ids = (i % 3 === 1) ? [inner[i], outer[i], outer[i + 1]] : [inner[i], outer[i], outer[i + 1], inner[i + 1]];
        if (i % 3 === 1) facets.push({ ids: [inner[i], outer[i + 1], inner[i + 1]], depth: WING_DEPTH_MAX * (i + 1) / WING_FACETS, prio: 2 + Math.floor(i / 2) });
        facets.push({ ids, depth: WING_DEPTH_MAX * i / WING_FACETS, prio: 2 + Math.floor(i / 2) });
      }
    }
    // The columns: two curved chains of panels rising and closing in.
    for (const sx of [-1, 1]) {
      const pts = [];
      for (let k = 0; k <= COLUMN_PANELS; k++) {
        const u = k / COLUMN_PANELS;
        const x = sx * (23.0 - 12.0 * u * u), y = DECK_Y + (EMBLEM_Y - 6 - DECK_Y) * u, z = Z - 1.5 - 4.0 * Math.sin(u * Math.PI);
        pts.push([V(x - sx * 1.4, y, z), V(x + sx * 1.4, y, z)]);
      }
      for (let k = 0; k < COLUMN_PANELS; k++) {
        facets.push({ ids: [pts[k][0], pts[k][1], pts[k + 1][1], pts[k + 1][0]], depth: 1.5 + 4.0 * Math.sin(((k + 0.5) / COLUMN_PANELS) * Math.PI), prio: 3 + (k % 2) * 3 });
      }
    }
    // Four triangles bridging the columns' tops to the emblem.
    const topL = V(-11.0, EMBLEM_Y - 6, Z - 1.5), topR = V(11.0, EMBLEM_Y - 6, Z - 1.5);
    const apex = V(0, EMBLEM_Y - 3, Z - 2.5), midL = V(-6, EMBLEM_Y - 8.5, Z - 3.5), midR = V(6, EMBLEM_Y - 8.5, Z - 3.5);
    facets.push({ ids: [topL, apex, midL], depth: 2.5, prio: 5 });
    facets.push({ ids: [midL, apex, midR], depth: 3.0, prio: 5 });
    facets.push({ ids: [midR, apex, topR], depth: 2.5, prio: 5 });
    facets.push({ ids: [midL, midR, V(0, EMBLEM_Y - 12, Z - 3.0)], depth: 3.0, prio: 6 });

    // The socle: a trapezoid from the deck (half-width 46) to the octagon's
    // foot (half-width 24), cut into nine chevron triangles between five
    // base points whose depth alternates by a unit and the six top points
    // the wings and blocks already own.
    const socleX = [-SOCLE_BASE_HALF_W, -24, -8, 8, 24, SOCLE_BASE_HALF_W];
    const T = [V(-24, SOCLE_TOP, Z - 1.5), V(-15.5, SOCLE_TOP, Z - 0.5), V(-8, SOCLE_TOP, Z - 0.5), V(8, SOCLE_TOP, Z - 0.5), V(15.5, SOCLE_TOP, Z - 0.5), V(24, SOCLE_TOP, Z - 1.5)];
    const B = [V(-46, DECK_Y, Z + 0.3), V(-23, DECK_Y, Z - 1.3), V(0, DECK_Y, Z - 0.9), V(23, DECK_Y, Z - 1.3), V(46, DECK_Y, Z + 0.3)];
    const tri = [[T[0], B[0], B[1]], [T[0], B[1], T[1]], [T[1], B[1], T[2]], [T[2], B[1], B[2]], [T[2], B[2], T[3]], [T[3], B[2], T[4]], [T[4], B[2], B[3]], [T[4], B[3], T[5]], [T[5], B[3], B[4]]];
    tri.forEach((ids, i) => facets.push({ ids, depth: 0.5 + (i % 2) * 1.0, prio: -1 }));
    for (const sx of [-1, 1]) {
      facets.push({ ids: [V(sx * 8, SOCLE_TOP, Z - 0.5), V(sx * 15.5, SOCLE_TOP, Z - 0.5), V(sx * 15.5, BLOCK_TOP, Z - 0.5), V(sx * 8, BLOCK_TOP, Z - 0.5)], depth: 0.5, prio: 0 });
    }
    // The podium: its LED front two and a half units out toward the crowd,
    // and a slanted panel a side running back to the socle's plane.
    const pz = Z + PODIUM_OUT, pw = PODIUM_HALF_W, ph = DECK_Y + PODIUM_H;
    facets.push({ ids: [V(-pw, DECK_Y, pz), V(pw, DECK_Y, pz), V(pw, ph, pz), V(-pw, ph, pz)], depth: -PODIUM_OUT, prio: -1 });
    for (const sx of [-1, 1]) {
      const ids = [V(sx * pw, DECK_Y, pz), V(sx * 12, DECK_Y, Z - 0.4), V(sx * 12, 6.6, Z - 0.4), V(sx * pw, ph, pz)];
      facets.push({ ids: sx < 0 ? ids.reverse() : ids, depth: -PODIUM_OUT * 0.5, prio: -1 });
    }

    // The emblem: a double chevron and a crown of triangles, bars only.
    const barOnlyEdges = [];
    const E = (a, b) => barOnlyEdges.push([a, b]);
    for (const dy of [0, 1.8]) {
      const l = V(-9, EMBLEM_Y - 4.0 + dy, Z - 1), m = V(0, EMBLEM_Y - 0.6 + dy, Z - 1), r = V(9, EMBLEM_Y - 4.0 + dy, Z - 1);
      E(l, m); E(m, r);
    }
    for (let i = -1; i <= 1; i++) {
      const a = V(i * 5 - 2.2, EMBLEM_Y + 1.6, Z - 1.2), b = V(i * 5 + 2.2, EMBLEM_Y + 1.6, Z - 1.2), c = V(i * 5, EMBLEM_Y + 4.2, Z - 1.2);
      E(a, b); E(b, c); E(c, a);
    }

    // Tier cut: keep the facets by priority.
    facets.sort((a, b) => a.prio - b.prio);
    const kept = facets.slice(0, Math.min(facets.length, layout.facets));
    this._facets = kept;
    this._verts = verts;

    // Distance of a facet's centre to the diamond, for PULSE; the global uv
    // of every vertex on the front plane's bounding box, for the mapping.
    const centre = (f) => { const c = [0, 0, 0]; for (const id of f.ids) { c[0] += verts[id][0]; c[1] += verts[id][1]; c[2] += verts[id][2]; } return c.map((v) => v / f.ids.length); };
    const gu = (p) => [(p[0] + STRUCTURE_HALF_W) / (2 * STRUCTURE_HALF_W), p[1] / STRUCTURE_TOP];

    const positions = [], uvG = [], uvL = [], fac = [], on = [], index = [];
    kept.forEach((f, fi) => {
      const c = centre(f);
      const dist = Math.hypot(c[0], c[1] - DIAMOND_CENTER_Y);
      f.dist = dist; f.index = fi;
      const base = positions.length / 3;
      // Local uv: the polygon's own bounding box, so the cells tile it.
      let minx = 1e9, maxx = -1e9, miny = 1e9, maxy = -1e9;
      for (const id of f.ids) { const p = verts[id]; minx = Math.min(minx, p[0]); maxx = Math.max(maxx, p[0]); miny = Math.min(miny, p[1]); maxy = Math.max(maxy, p[1]); }
      const side = Math.max(maxx - minx, maxy - miny);
      const cells = Math.max(4, Math.round(side * LED_CELLS_PER_UNIT));
      for (const id of f.ids) {
        const p = verts[id];
        positions.push(p[0], p[1], p[2]);
        const g = gu(p); uvG.push(g[0], g[1]);
        uvL.push((p[0] - minx) / side, (p[1] - miny) / side);
        fac.push(fi, f.depth, dist, cells);
        on.push(1);
      }
      for (let k = 1; k + 1 < f.ids.length; k++) index.push(base, base + k, base + k + 1);
    });
    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(index);
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    geometry.setAttribute('aUvGlobal', new THREE.BufferAttribute(new Float32Array(uvG), 2));
    geometry.setAttribute('aUvLocal', new THREE.BufferAttribute(new Float32Array(uvL), 2));
    geometry.setAttribute('aFacet', new THREE.BufferAttribute(new Float32Array(fac), 4));
    this._facetOn = new Float32Array(on);
    geometry.setAttribute('aOn', new THREE.BufferAttribute(this._facetOn, 1));
    this._facetGeometry = geometry;
    this._facetVisible = new Float32Array(kept.length).fill(1);
    this._facetTarget = new Float32Array(kept.length).fill(1);

    const blank = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1);
    blank.needsUpdate = true;
    this._blankTexture = blank;
    this._facetUniforms = Object.assign({}, safeZoneUniforms, {
      uResolution: { value: new THREE.Vector2(1280, 720) },
      uArtwork: { value: blank },
      uHasArt: { value: 0 },
      uColor0: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uColor1: { value: new THREE.Color(FALLBACK_COLORS[1]) },
      uColor2: { value: new THREE.Color(FALLBACK_COLORS[2]) },
      uMode: { value: CONTENT.solid },
      uModePrev: { value: CONTENT.solid },
      uModeMix: { value: 1 },
      uDrop: { value: 0 },
      uPulseT: { value: 0 },
      uTime: { value: 0 },
      uBrightness: { value: 0.8 },
      uNoise: { value: 0 }
    });
    this._facetMaterial = new THREE.ShaderMaterial({
      uniforms: this._facetUniforms,
      vertexShader: facetVertexShader,
      fragmentShader: facetFragmentShader,
      side: THREE.DoubleSide, depthTest: true, depthWrite: true
    });
    const mesh = new THREE.Mesh(geometry, this._facetMaterial);
    mesh.frustumCulled = false;
    const self = this;
    mesh.onBeforeRender = function (renderer) {
      renderer.getDrawingBufferSize(_size);
      self._facetUniforms.uResolution.value.set(_size.x, _size.y);
      if (self._cityUniforms) self._cityUniforms.uResolution.value.set(_size.x, _size.y);
    };
    this._facetMesh = mesh;
    this._scene.add(mesh);

    // The edge graph: every facet edge once, plus the emblem's bars.
    const edges = [], emap = new Map(), adj = new Map();
    const addEdge = (a, b, fi) => {
      const key = a < b ? a + '_' + b : b + '_' + a;
      let e = emap.get(key);
      if (!e) {
        const pa = verts[a], pb = verts[b];
        e = { a, b, id: edges.length, facets: [], len: Math.hypot(pa[0] - pb[0], pa[1] - pb[1], pa[2] - pb[2]) };
        edges.push(e); emap.set(key, e);
        if (!adj.has(a)) adj.set(a, []); if (!adj.has(b)) adj.set(b, []);
        adj.get(a).push(e); adj.get(b).push(e);
      }
      if (fi != null) e.facets.push(fi);
      return e;
    };
    kept.forEach((f, fi) => { f.edges = []; for (let k = 0; k < f.ids.length; k++) f.edges.push(addEdge(f.ids[k], f.ids[(k + 1) % f.ids.length], fi)); });
    for (const [a, b] of barOnlyEdges) addEdge(a, b, null);
    this._edges = edges;
    this._adj = adj;

    // Where fixtures can be mounted: the wings' outer polylines, the
    // columns' joints and the octagon's upper vertices - in front of the
    // facet they sit on, so the housing shows against it.
    const mounts = [];
    for (const sx of [-1, 1]) {
      for (let i = 0; i <= WING_FACETS; i++) {
        const u = i / WING_FACETS;
        mounts.push([sx * (24.0 + 17.0 * u), SOCLE_TOP + 29.0 * u, Z - 0.5 - WING_DEPTH_MAX * u - 1.0 + 0.3]);
      }
      for (let k = 1; k <= COLUMN_PANELS; k++) {
        const u = k / COLUMN_PANELS;
        mounts.push([sx * (23.0 - 12.0 * u * u), DECK_Y + (EMBLEM_Y - 6 - DECK_Y) * u, Z - 1.5 - 4.0 * Math.sin(u * Math.PI) + 0.3]);
      }
    }
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2 + Math.PI / 8;
      if (Math.sin(a) > 0.2) mounts.push([Math.cos(a) * OCTAGON_R_OUT, cy + Math.sin(a) * OCTAGON_R_OUT, Z - OCTAGON_DEPTH + 0.3]);
    }
    this._mounts = mounts;

    // The truss: dark bars from the deck up to the wings and columns, and
    // cables from the emblem down to the wing tips.
    const lines = [];
    const L = (a, b) => lines.push(a[0], a[1], a[2], b[0], b[1], b[2]);
    // Only the cables from the emblem down to the wings: no pillars, no
    // rails - they read as scaffolding around the stage, not as part of it.
    for (const sx of [-1, 1]) {
      for (let i = 2; i <= WING_FACETS; i += 2) {
        const u = i / WING_FACETS;
        const p = [sx * (24.0 + 17.0 * u), SOCLE_TOP + 29.0 * u, Z - 0.5 - WING_DEPTH_MAX * u - 1.0];
        L([0, EMBLEM_Y + 4, Z - 2], [p[0], p[1] + 0.5, p[2]]);
      }
    }
    const lg = new THREE.BufferGeometry();
    lg.setAttribute('position', new THREE.BufferAttribute(new Float32Array(lines), 3));
    this._trussGeometry = lg;
    this._trussMaterial = new THREE.LineBasicMaterial({ color: 0x2a2e38 });
    this._truss = new THREE.LineSegments(lg, this._trussMaterial);
    this._truss.frustumCulled = false;
    this._scene.add(this._truss);
  }

  _buildBars() {
    const edges = this._edges, verts = this._verts;
    const n = edges.length * BAR_PIXELS;
    const positions = new Float32Array(n * 3);
    const seeds = new Float32Array(n);
    this._barLevel = new Float32Array(n);
    let i = 0;
    for (const e of edges) {
      const pa = verts[e.a], pb = verts[e.b];
      for (let k = 0; k < BAR_PIXELS; k++) {
        const t = (k + 0.5) / BAR_PIXELS;
        positions[i * 3] = pa[0] + (pb[0] - pa[0]) * t;
        positions[i * 3 + 1] = pa[1] + (pb[1] - pa[1]) * t;
        positions[i * 3 + 2] = pa[2] + (pb[2] - pa[2]) * t + BAR_LIFT;
        seeds[i] = Math.random();
        i++;
      }
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
    this._barLevelAttr = new THREE.BufferAttribute(this._barLevel, 1);
    this._barLevelAttr.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('aLevel', this._barLevelAttr);
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 24, STAGE_Z - 4), 60);
    this._barGeometry = geometry;
    this._barUniforms = Object.assign({}, safeZoneUniforms, {
      uStrobe: { value: 0 },
      uSizeScale: { value: 1 },
      uPointSizeMax: { value: Math.max(1, getMaxPointSize()) },
      uBarColor: { value: new THREE.Color(FALLBACK_COLORS[1]) }
    });
    this._barMaterial = new THREE.ShaderMaterial({
      uniforms: this._barUniforms, vertexShader: barVertexShader, fragmentShader: barFragmentShader,
      transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending
    });
    this._bars = new THREE.Points(geometry, this._barMaterial);
    this._bars.frustumCulled = false;
    this._bars.renderOrder = 1;
    this._scene.add(this._bars);
    this._barCount = n;
    // The runners, pre-allocated.
    this._runners = [];
    for (let r = 0; r < CHASE_RUNNERS; r++) this._runners.push({ live: false, edge: null, t: 0, dir: 1, life: 0, facet: -1, k: 0 });
  }

  // The heads are mounted ON the structure: `count` of the mount points,
  // taken evenly across them sorted left to right, so a smaller tier keeps
  // the same spread with fewer fixtures.
  _buildLyres(count) {
    const all = this._mounts.slice().sort((a, b) => a[0] - b[0]);
    const n = Math.min(count, all.length);
    const picked = [];
    for (let i = 0; i < n; i++) picked.push(all[Math.floor((i + 0.5) * all.length / n)]);
    const cols = n, rows = 1;
    const quad = new Float32Array([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0]);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    const iCol = new Float32Array(n), iPos = new Float32Array(n * 3), iAxis = new Float32Array(n * 2);
    for (let i = 0; i < n; i++) {
      const p = picked[i];
      iCol[i] = i; iPos[i * 3] = p[0]; iPos[i * 3 + 1] = p[1]; iPos[i * 3 + 2] = p[2];
      iAxis[i * 2] = p[0] / 41; iAxis[i * 2 + 1] = (p[1] - 22) / 18;
    }
    geometry.setAttribute('iCol', new THREE.InstancedBufferAttribute(iCol, 1));
    geometry.setAttribute('iPos', new THREE.InstancedBufferAttribute(iPos, 3));
    geometry.setAttribute('iAxis', new THREE.InstancedBufferAttribute(iAxis, 2));
    geometry.instanceCount = n;
    this._lyrePositions = picked;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 40, STAGE_Z), 90);
    this._lyreGeometry = geometry;
    this._lyreUniforms = Object.assign({}, safeZoneUniforms, {
      uCols: { value: cols }, uRows: { value: rows },
      uPat: { value: LYRE_PAT.rain }, uPatPrev: { value: LYRE_PAT.rain }, uPatMix: { value: 1 },
      uStep: { value: 0 }, uStepMix: { value: 1 },
      uConverge: { value: new THREE.Vector3(0, 60, -20) }, uConvergePrev: { value: new THREE.Vector3(0, 60, -20) },
      uBurst: { value: 0 }, uSpread: { value: 1 },
      uMasterGain: { value: 0 }, uColorA: { value: new THREE.Color(FALLBACK_COLORS[1]) }, uWhite: { value: 0 },
      uPixelScale: { value: 800 }, uPulse: { value: 1 }
    });
    this._lyreMaterial = new THREE.ShaderMaterial({
      uniforms: this._lyreUniforms, vertexShader: lyreVertexShader, fragmentShader: lyreFragmentShader,
      transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending, side: THREE.DoubleSide
    });
    this._lyres = new THREE.Mesh(geometry, this._lyreMaterial);
    this._lyres.frustumCulled = false;
    this._lyres.renderOrder = 3;
    this._scene.add(this._lyres);
    // No housings: a box per head read as a crude prop. The beams leave the
    // structure's own edges - the mount points ARE its vertices.
  }

  _buildLasers() {
    const n = LASER_EMITTERS * LASER_PER_EMITTER;
    const quad = new Float32Array([-1, 0, 0, 1, 0, 0, 1, 1, 0, -1, 1, 0]);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    const iEmitter = new Float32Array(n), iIndex = new Float32Array(n), iSeed = new Float32Array(n);
    for (let i = 0; i < n; i++) { iEmitter[i] = Math.floor(i / LASER_PER_EMITTER); iIndex[i] = i % LASER_PER_EMITTER; iSeed[i] = Math.random(); }
    geometry.setAttribute('iEmitter', new THREE.InstancedBufferAttribute(iEmitter, 1));
    geometry.setAttribute('iIndex', new THREE.InstancedBufferAttribute(iIndex, 1));
    geometry.setAttribute('iSeed', new THREE.InstancedBufferAttribute(iSeed, 1));
    geometry.instanceCount = n;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 20, -20), 90);
    this._laserGeometry = geometry;
    this._laserUniforms = Object.assign({}, safeZoneUniforms, {
      uEmitter: { value: [new THREE.Vector3(0, DIAMOND_CENTER_Y + OCTAGON_R_OUT + 1.5, STAGE_Z - 2), new THREE.Vector3(-11, EMBLEM_Y - 6, STAGE_Z - 1.5), new THREE.Vector3(11, EMBLEM_Y - 6, STAGE_Z - 1.5)] },
      uLaserMode: { value: 0 }, uLaserPhase: { value: 0 }, uLaserSpread: { value: 0.9 },
      uMasterGain: { value: 0 },
      uColorA: { value: new THREE.Color(FALLBACK_COLORS[1]) }, uColorB: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uPixelScale: { value: 800 }, uOuterPx: { value: 6 }, uCorePx: { value: 2 },
      uTime: { value: 0 }, uPulse: { value: 1 }
    });
    this._laserMaterial = new THREE.ShaderMaterial({
      uniforms: this._laserUniforms, vertexShader: laserVertexShader, fragmentShader: laserFragmentShader,
      transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending, side: THREE.DoubleSide
    });
    this._lasers = new THREE.Mesh(geometry, this._laserMaterial);
    this._lasers.frustumCulled = false;
    this._lasers.renderOrder = 4;
    this._scene.add(this._lasers);
  }

  _buildPyro() {
    this._pyro = new PyroLayer({
      // No smoke: at this scale a lit puff is a white block the size of the
      // podium, not haze. No reflection: the deck's top is edge-on.
      // No trails either: through the bloom a gerb's streaks read as one
      // white block over the fountain.
      max: PYRO_MAX, trails: false, smoke: false, reflection: false, crackle: true, flames: false,
      linewidth: 1.5, space: 'world', floorKill: true, smokeLift: 0.24,
      world: { x: 0, y: DECK_Y + 0.3, z: STAGE_Z + 1.0, scale: PYRO_SCALE, fog: 0.0 }
    });
    this._pyro.init(this._scene);
    // The plane's view depth, the reference the point size is computed for.
    _v3.set(0, DECK_Y + 0.3, STAGE_Z + 1.0).applyMatrix4(this._camera.matrixWorldInverse);
    this._pyroDepth = Math.max(0.001, -_v3.z);
    this._pyro.setWorld(0, DECK_Y + 0.3, STAGE_Z + 1.0, PYRO_SCALE, this._pyroDepth);
    this._pyroNozzles = PYRO_NOZZLE_X.map((x) => ({ x: x / PYRO_SCALE, y: PYRO_GROUND_Y, tilt: 0, dx: 0, dy: 1 }));
    this._pyroTurn = 0;
    this._pyroQueue = [];
    if (CONFETTI_MAX[this._tier] && urlParam('confettioff') !== '1') {
      this._confetti = new ConfettiLayer({ max: CONFETTI_MAX[this._tier], ribbons: true, unit: CONFETTI_UNIT, groundY: DECK_Y + 0.3, fog: 0, ambient: 0.05, depthTest: true, renderOrder: 5 });
      this._confetti.init(this._scene);
      this._confettiTurn = 0;
      this._confettiFlash = 0;
      // six of the lyre mounts, spread across the truss, as the paper's lights
      const all = this._lyrePositions || [];
      this._confettiMounts = [];
      for (let i = 0; i < CONFETTI_LIGHTS && all.length; i++) { const p = all[Math.floor((i + 0.5) * all.length / CONFETTI_LIGHTS)]; this._confettiMounts.push(new THREE.Vector3(p[0], p[1], p[2])); }
    }
  }

  _confettiCannon(k) {
    const x = PYRO_NOZZLE_X[CONFETTI_CANNONS[k]];
    return { origin: new THREE.Vector3(x, DECK_Y + 0.3, STAGE_Z + 1.0), direction: new THREE.Vector3(-x * 0.01, 1, 0.3).normalize() };
  }
  _confettiShot(cannons, strength, frac, ribbons) {
    const C = this._confetti;
    if (!C) return;
    const list = cannons.map((k) => this._confettiCannon(k));
    for (const c of list) C.fire(c.origin, c.direction, strength, frac);
    if (ribbons) C.fireRibbons(list);
  }
  // The virtual beams: from the mounts toward the crowd, fanning slowly, in
  // the lyres' colour at the lyres' gain.
  _placeConfettiLights(t) {
    const C = this._confetti;
    if (!C || !this._confettiMounts) return;
    C.setCamera(this._camera);
    const col = this._lyreUniforms.uColorA.value;
    const gain = this._lyreGainTarget != null ? this._lyreGainTarget : 0.7;
    for (let i = 0; i < this._confettiMounts.length; i++) {
      const m = this._confettiMounts[i];
      _v3.set(m.x * 0.4 + 10 * Math.sin(t * 0.35 + i * 1.3), DECK_Y + 4, STAGE_Z + 26 + 8 * Math.sin(t * 0.27 + i * 0.9)).sub(m);
      C.setLight(i, m, _v3, col, 0.5 + 1.2 * gain);
    }
  }

  _buildCo2() {
    const n = CO2_PUFFS * 2;
    const seeds = new Float32Array(n), cannon = new Float32Array(n), positions = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { seeds[i] = Math.random(); cannon[i] = i < CO2_PUFFS ? 0 : 1; }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aSeed', new THREE.BufferAttribute(seeds, 1));
    geometry.setAttribute('aCannon', new THREE.BufferAttribute(cannon, 1));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 8, STAGE_Z), 40);
    this._co2Geometry = geometry;
    this._co2Uniforms = {
      uTime: { value: 0 }, uStart: { value: -1 }, uCannonX: { value: new THREE.Vector2(-CO2_X, CO2_X) },
      uSizeScale: { value: 1 }, uPointSizeMax: { value: Math.max(1, getMaxPointSize()) }
    };
    this._co2Material = new THREE.ShaderMaterial({
      uniforms: this._co2Uniforms, vertexShader: co2VertexShader, fragmentShader: co2FragmentShader,
      transparent: true, depthWrite: false, depthTest: true, blending: THREE.NormalBlending
    });
    this._co2 = new THREE.Points(geometry, this._co2Material);
    this._co2.frustumCulled = false;
    this._co2.renderOrder = 5;
    this._scene.add(this._co2);
  }

  // The DJ booth: a podium and a console in silhouette, a row of diodes on
  // its face. The human scale of the stage, and its focal point.
  _buildBooth() {
    const dark = new THREE.MeshBasicMaterial({ color: 0x07080b });
    // The podium's top: a dark slab over the LED front and sides.
    const podium = new THREE.Mesh(new THREE.BoxGeometry(PODIUM_HALF_W * 2 + 0.4, 0.3, PODIUM_OUT + 1.2), dark);
    podium.position.set(0, DECK_Y + PODIUM_H + 0.15, STAGE_Z + PODIUM_OUT / 2 - 0.5);
    const console_ = new THREE.Mesh(new THREE.BoxGeometry(9.5, 1.5, 2.6), dark);
    console_.position.set(0, DECK_Y + PODIUM_H + 0.3 + 0.75, BOOTH_Z);
    const top = new THREE.Mesh(new THREE.BoxGeometry(10.2, 0.28, 3.2), dark);
    top.position.set(0, DECK_Y + PODIUM_H + 0.3 + 1.6, BOOTH_Z);
    const diodeGeo = new THREE.BoxGeometry(0.34, 0.2, 0.1);
    const diodeMat = new THREE.MeshBasicMaterial({ color: FALLBACK_COLORS[1] });
    const diodes = new THREE.InstancedMesh(diodeGeo, diodeMat, 16);
    const m = new THREE.Matrix4();
    for (let i = 0; i < 16; i++) { m.makeTranslation(-4.2 + i * 0.56, DECK_Y + PODIUM_H + 0.3 + 0.95 + (i % 2) * 0.3, BOOTH_Z + 1.36); diodes.setMatrixAt(i, m); }
    diodes.instanceMatrix.needsUpdate = true;
    this._booth = [podium, console_, top, diodes];
    this._boothMaterials = [dark, diodeMat];
    this._boothGeometries = [podium.geometry, console_.geometry, top.geometry, diodeGeo];
    this._diodeMaterial = diodeMat;
    for (const o of this._booth) { o.frustumCulled = false; this._scene.add(o); }
  }

  _buildDeck() {
    const geometry = new THREE.BoxGeometry(80, DECK_Y, 30);
    const material = new THREE.MeshBasicMaterial({ color: 0x06070a });
    const deck = new THREE.Mesh(geometry, material);
    deck.position.set(0, DECK_Y / 2, STAGE_Z - 14);
    this._deck = deck;
    this._deckGeometry = geometry;
    this._deckMaterial = material;
    this._scene.add(deck);
  }

  _buildCity() {
    const depth = CITY_DEPTH;
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(this._camera.fov) / 2);
    const h = 2 * depth * tanHalf * 1.4;
    const w = h * Math.max(this._camera.aspect, 16 / 9) * 1.1;
    const geometry = new THREE.PlaneGeometry(w, h);
    // Where the bay's waterline lands on the plane: the deck's level, seen
    // from the camera, plus a little so the water shows behind the stage.
    const pitch = THREE.MathUtils.degToRad(CAMERA_PITCH_DEG);
    const ndcY = ((DECK_Y - CAMERA_POS[1]) / depth - Math.tan(pitch)) / tanHalf;
    const horizon = 0.5 + ndcY / 2.8;
    this._cityUniforms = Object.assign({}, safeZoneUniforms, {
      uResolution: { value: new THREE.Vector2(1280, 720) },
      uSkyTop: { value: new THREE.Color(0x03040c) },
      uSkyHorizon: { value: new THREE.Color(0x0d0c1a) },
      uHazeColor: { value: new THREE.Color(HAZE_FALLBACK) },
      uPal0: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uPal1: { value: new THREE.Color(FALLBACK_COLORS[1]) },
      uPal2: { value: new THREE.Color(FALLBACK_COLORS[2]) },
      uStageGlow: { value: new THREE.Color(0x202030) },
      uHorizon: { value: horizon },
      uSeed: { value: Math.random() * 100 },
      uTime: { value: 0 },
      uHigh: { value: 0 },
      uBarPulse: { value: 0 },
      uProcedural: { value: 1 }
    });
    this._cityMaterial = new THREE.ShaderMaterial({
      uniforms: this._cityUniforms, vertexShader: cityVertexShader, fragmentShader: cityFragmentShader,
      depthWrite: false, depthTest: true
    });
    const mesh = new THREE.Mesh(geometry, this._cityMaterial);
    const dir = new THREE.Vector3();
    this._camera.getWorldDirection(dir);
    mesh.position.copy(this._camera.position).addScaledVector(dir, depth);
    mesh.quaternion.copy(this._camera.quaternion);
    mesh.frustumCulled = false;
    // After everything opaque: its shader has no discard, so early-Z rejects
    // what the structure and the crowd already covered.
    mesh.renderOrder = 0;
    this._city = mesh;
    this._cityGeometry = geometry;
    this._scene.add(mesh);
  }

  // The photograph on its plane, and the haze layer in front of it.
  _buildPlate(assets) {
    const m = assets.manifest, camera = this._camera;
    const depth = PLATE_DEPTH;
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    const aspect = Math.max(camera.aspect, 16 / 9);
    const frustumW = 2 * depth * tanHalf * aspect;
    const w = frustumW * PLATE_WIDTH_MARGIN;
    const imgH = w * m.size[1] / m.size[0];
    const vMin = -PLATE_WATER;
    const h = imgH * (1 - vMin);
    // the ground line (v = 1 - m.ground, three flips the picture) on the
    // scene's floor line at this depth
    const groundV = 1 - m.ground;
    const pitch = THREE.MathUtils.degToRad(CAMERA_PITCH_DEG);
    const floorNdc = ((DECK_Y - CAMERA_POS[1]) / depth - Math.tan(pitch)) / tanHalf;
    const floorY = floorNdc * depth * tanHalf;             // camera space, at the plate
    const centreV = (vMin + 1) / 2;
    const centreY = floorY + (centreV - groundV) * imgH;
    const geometry = new THREE.PlaneGeometry(w, h);
    const lit = new Uint8Array(Math.max(1, m.towers) * 4);
    for (let i = 0; i < lit.length; i += 4) { lit[i] = Math.round(255 / CITY_LIT_MAX); lit[i + 3] = 255; }
    this._cityLitData = lit;
    this._cityLitTex = new THREE.DataTexture(lit, Math.max(1, m.towers), 1, THREE.RGBAFormat);
    this._cityLitTex.minFilter = this._cityLitTex.magFilter = THREE.NearestFilter;
    this._cityLitTex.needsUpdate = true;
    this._cityLit = new Float32Array(Math.max(1, m.towers)).fill(1);
    this._cityTowerX = (m.towerList || []).map((t) => t.x);
    this._cityFlickerPhase = this._cityTowerX.map((x, i) => (i * 0.618034) % 1);
    this._plateUniforms = Object.assign({}, safeZoneUniforms, {
      uResolution: this._cityUniforms.uResolution,
      uOn: { value: assets.on }, uOff: { value: assets.off }, uIds: { value: assets.ids }, uLit: { value: this._cityLitTex },
      uTowers: { value: Math.max(1, m.towers) }, uStatic: { value: assets.off && assets.ids ? 0 : 1 },
      uGround: { value: groundV }, uVMin: { value: vMin },
      uHazeColor: this._cityUniforms.uHazeColor, uTint: { value: new THREE.Color(1, 1, 1) },
      uSceneLight: { value: new THREE.Vector3() }, uStageGlow: this._cityUniforms.uStageGlow,
      uTexel: { value: new THREE.Vector2(1 / assets.on.image.width, 1 / assets.on.image.height) },
      uTime: { value: 0 }, uMaskDebug: { value: this._plateMaskDebug ? 1 : 0 }, uContrastKeep: { value: 1 }
    });
    // OPAQUE, with discard: it writes depth where the city is, which is what
    // lets a beam vanish behind a tower and stay in front of the plate when
    // it is nearer. toneMapped as the rest of the scene.
    this._plateMaterial = new THREE.ShaderMaterial({
      uniforms: this._plateUniforms, vertexShader: plateVertexShader, fragmentShader: plateFragmentShader,
      depthWrite: true, depthTest: true, toneMapped: true
    });
    const mesh = new THREE.Mesh(geometry, this._plateMaterial);
    const dir = new THREE.Vector3();
    camera.getWorldDirection(dir);
    const up = new THREE.Vector3(0, 1, 0).applyQuaternion(camera.quaternion);
    mesh.position.copy(camera.position).addScaledVector(dir, depth).addScaledVector(up, centreY);
    mesh.quaternion.copy(camera.quaternion);
    mesh.frustumCulled = false;
    mesh.renderOrder = -1;   // before the sky plane: early-Z then rejects the sky behind the towers
    this._plate = mesh;
    this._plateGeometry = geometry;
    this._plateFloorNdc = floorNdc;
    this._scene.add(mesh);
    this._cityUniforms.uProcedural.value = 0;

    // the haze layer, a little nearer, its floor line at the same height on screen
    const hd = HAZE_LAYER_DEPTH;
    const hw = 2 * hd * tanHalf * aspect * PLATE_WIDTH_MARGIN, hh = 2 * hd * tanHalf * 1.3;
    const hFloorY = floorNdc * hd * tanHalf;
    const hCentreY = hFloorY + hh * 0.25;
    this._hazeUniforms = { uHazeColor: this._cityUniforms.uHazeColor, uGroundV: { value: 0.25 } };
    this._hazeMaterial = new THREE.ShaderMaterial({ uniforms: this._hazeUniforms, vertexShader: hazeLayerVertexShader, fragmentShader: hazeLayerFragmentShader, transparent: true, depthWrite: false, depthTest: true });
    const haze = new THREE.Mesh(new THREE.PlaneGeometry(hw, hh), this._hazeMaterial);
    haze.position.copy(camera.position).addScaledVector(dir, hd).addScaledVector(up, hCentreY);
    haze.quaternion.copy(camera.quaternion);
    haze.frustumCulled = false;
    haze.renderOrder = 1;    // after the plate, before the beams (3) and lasers (4)
    this._hazeLayer = haze;
    this._scene.add(haze);
    this._applyPlateTint();
  }

  _applyPlateTint() {
    if (!this._plateUniforms) return;
    const sky = this._cityUniforms.uSkyHorizon.value, p0 = this._cityUniforms.uPal0.value;
    const tint = new THREE.Color(sky.r, sky.g, sky.b).multiplyScalar(4).add(new THREE.Color(p0.r, p0.g, p0.b).multiplyScalar(0.6));
    const mx = Math.max(tint.r, tint.g, tint.b, 1e-3);
    this._plateUniforms.uTint.value.set(0.35 + 0.65 * tint.r / mx, 0.35 + 0.65 * tint.g / mx, 0.35 + 0.65 * tint.b / mx);
  }

  // THE LIVING WINDOWS: one level per tower, written to the lit texture.
  //   rest       1, one tower in six breathing +-10% over 8 beats
  //   build      the towers go out from the outside in, down to 10%, as the
  //              drop approaches (the mark's msUntil, else the build's time)
  //   drop       the whole city at 1.3 for a bar, then 1
  //   phrase     a wave of light crossing left to right in one beat
  _updateCity(t, beat, seg, dropMsUntil) {
    if (!this._cityLit) return;
    const n = this._cityLit.length, xs = this._cityTowerX;
    // the montee: the build segment's own clock, or a drop announced within
    // CITY_BUILD_BEATS - the mark is the montee's signal whatever the segment
    let buildP = 0;
    if (seg === 'build') {
      if (this._buildSince < 0) this._buildSince = t;
      buildP = Math.min(1, (t - this._buildSince) / (CITY_BUILD_BEATS * beat));
    } else this._buildSince = -1;
    if (dropMsUntil != null && dropMsUntil >= 0) buildP = Math.max(buildP, 1 - dropMsUntil / (CITY_BUILD_BEATS * beat * 1000));
    const dropAge = t - this._cityDropAt;
    const dropLevel = dropAge >= 0 && dropAge < 4 * beat ? CITY_DROP_LEVEL : (dropAge < 5 * beat ? 1 + (CITY_DROP_LEVEL - 1) * (1 - (dropAge - 4 * beat) / beat) : 1);
    const waveX = (t - this._cityWaveAt) / beat;   // 0..1 across the city in one beat
    for (let i = 0; i < n; i++) {
      let lit = 1;
      if (i % CITY_FLICKER_EVERY === 0) lit += CITY_FLICKER * Math.sin((t / (8 * beat) + this._cityFlickerPhase[i]) * Math.PI * 2);
      // outside first: the rank by distance from the centre
      const outer = Math.abs((xs[i] != null ? xs[i] : 0.5) - 0.5) * 2;   // 1 at the edges
      const off = buildP > 0 ? clamp01((buildP - (1 - outer)) * 3) : 0;
      lit *= 1 - (1 - CITY_BUILD_FLOOR) * off;
      if (dropLevel > 1) lit = Math.max(lit, dropLevel);
      if (waveX >= 0 && waveX < 1.3) { const x = xs[i] != null ? xs[i] : 0.5; lit += CITY_WAVE_GAIN * Math.exp(-Math.pow((x - waveX) * 6, 2)); }
      if (this._forceCityLight != null) lit = this._forceCityLight;
      this._cityLit[i] = Math.min(CITY_LIT_MAX, Math.max(0, lit));
      this._cityLitData[i * 4] = Math.round(this._cityLit[i] / CITY_LIT_MAX * 255);
    }
    this._cityLitTex.needsUpdate = true;
  }

  // CrowdWave's crowd, seen from behind, 25 rows deep.
  _buildCrowd(layout) {
    const atlas = this._atlas, meta = atlas.meta, camera = this._camera;
    const usable = meta.frames.filter((f) => !f.empty);
    const meanWidthFrac = usable.reduce((s, f) => s + f.widthFrac, 0) / usable.length;
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2);
    const aspect = Math.max(camera.aspect, 16 / 9);
    const rows = [];
    const ratio = Math.pow(Z_FAR / Z_NEAR, 1 / (layout.rows - 1));
    let widthSum = 0;
    for (let r = 0; r < layout.rows; r++) {
      const z = Z_NEAR * Math.pow(ratio, r), depth = -z;
      const width = 2 * depth * tanHalf * aspect * SPAN_OVERSHOOT;
      rows.push({ z, depth, width, gap: r > 0 ? rows[r - 1].z - z : 0 });
      widthSum += width;
    }
    const spacing = widthSum / layout.crowd;
    let total = 0;
    for (const row of rows) { row.count = Math.max(1, Math.round(row.width / spacing)); total += row.count; }
    rows[rows.length - 1].count += layout.crowd - total;
    const N = layout.crowd;
    const height = Math.min(PERSON_HEIGHT, Math.max(PERSON_HEIGHT * 0.6, spacing / (meanWidthFrac * (1 - OVERLAP))));
    const positions = new Float32Array(N * 3), heights = new Float32Array(N), spriteCalm = new Float32Array(N), spriteUp = new Float32Array(N);
    const seeds = new Float32Array(N), periods = new Float32Array(N), axes = new Float32Array(N * 2);
    const phone = { pos: [], seed: [], period: [], height: [], left: [], centre: [], axis: [] };
    const calmSprites = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9], upSprites = [10, 11, 12, 13], phoneSprites = [14, 15];
    const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
    const zSpan = Z_NEAR - Z_FAR;
    let i = 0;
    for (let r = 0; r < rows.length; r++) {
      const row = rows[r], step = row.width / row.count, left = -row.width / 2 + step / 2;
      const gap = r > 0 ? row.gap : rows[1].gap;
      for (let n = 0; n < row.count; n++) {
        const h = height * (1 + (Math.random() * 2 - 1) * SCALE_JITTER);
        const x = left + n * step + (Math.random() - 0.5) * step * 0.6;
        const z = row.z + (Math.random() - 0.5) * gap * ROW_JITTER;
        const hasPhone = Math.random() < PHONE_FRACTION;
        positions[i * 3] = x; positions[i * 3 + 1] = 0; positions[i * 3 + 2] = z;
        heights[i] = h;
        spriteCalm[i] = hasPhone ? pick(phoneSprites) : pick(calmSprites);
        spriteUp[i] = hasPhone ? pick(phoneSprites) : pick(upSprites);
        seeds[i] = Math.random();
        periods[i] = SWAY_PERIOD[0] + Math.random() * (SWAY_PERIOD[1] - SWAY_PERIOD[0]);
        const u0 = (Z_NEAR - z) / zSpan, nx = x / (row.width / 2 / SPAN_OVERSHOOT);
        axes[i * 2] = u0; axes[i * 2 + 1] = nx;
        if (hasPhone) {
          phone.pos.push(x + (Math.random() - 0.5) * h * 0.2, h * (0.92 + Math.random() * 0.1), z - 0.02);
          phone.seed.push(seeds[i]); phone.period.push(periods[i]); phone.height.push(h);
          phone.left.push(nx < 0 ? 1 : 0); phone.centre.push(Math.abs(nx) < 0.34 ? 1 : 0);
          phone.axis.push(u0, nx);
        }
        i++;
      }
    }
    this._crowdCount = N;
    const quad = new Float32Array([-0.5, 0, 0, 0.5, 0, 0, 0.5, 1, 0, -0.5, 1, 0]);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    geometry.setAttribute('iPos', new THREE.InstancedBufferAttribute(positions, 3));
    geometry.setAttribute('iHeight', new THREE.InstancedBufferAttribute(heights, 1));
    geometry.setAttribute('iSpriteCalm', new THREE.InstancedBufferAttribute(spriteCalm, 1));
    geometry.setAttribute('iSpriteUp', new THREE.InstancedBufferAttribute(spriteUp, 1));
    geometry.setAttribute('iSeed', new THREE.InstancedBufferAttribute(seeds, 1));
    geometry.setAttribute('iPeriod', new THREE.InstancedBufferAttribute(periods, 1));
    geometry.setAttribute('iAxis', new THREE.InstancedBufferAttribute(axes, 2));
    geometry.instanceCount = N;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 1, (Z_NEAR + Z_FAR) / 2), 80);
    this._crowdGeometry = geometry;
    const size = meta.size;
    this._waveUniforms = {
      uWave: { value: this._waves },
      uWaveSeconds: { value: WAVE_BEATS * this._beatSeconds },
      uHoldSeconds: { value: WAVE_HOLD_BEATS * this._beatSeconds }
    };
    this._crowdUniforms = Object.assign({}, safeZoneUniforms, this._waveUniforms, {
      uTime: { value: 0 }, uSway: { value: 1 }, uAllUp: { value: 0 },
      uAtlas: { value: atlas.texture },
      uAtlasGrid: { value: new THREE.Vector4(meta.cell / size, meta.margin / size, 0.5 / size, meta.grid) },
      uPixelScale: { value: 540 / Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2) },
      uRimColor: { value: new THREE.Color(FALLBACK_COLORS[0]) },
      uHazeColor: { value: new THREE.Color(HAZE_FALLBACK) },
      uRimPulse: { value: 1 }, uCrowdLight: { value: 1 },
      uLaserSheet: { value: new THREE.Vector4(30, 0.3, 0, STAGE_Z - 2) },
      uLaserColor: { value: new THREE.Color(FALLBACK_COLORS[1]) }
    });
    this._crowdMaterial = new THREE.ShaderMaterial({
      uniforms: this._crowdUniforms, vertexShader: crowdVertexShader, fragmentShader: crowdFragmentShader,
      transparent: false, depthWrite: true, depthTest: true, side: THREE.DoubleSide
    });
    this._crowd = new THREE.Mesh(geometry, this._crowdMaterial);
    this._crowd.frustumCulled = false;
    this._crowd.renderOrder = -1;
    this._scene.add(this._crowd);
    if (phone.seed.length > 0) this._buildPhones(phone);
  }

  _buildPhones(p) {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(p.pos), 3));
    geometry.setAttribute('iSeed', new THREE.BufferAttribute(new Float32Array(p.seed), 1));
    geometry.setAttribute('iPeriod', new THREE.BufferAttribute(new Float32Array(p.period), 1));
    geometry.setAttribute('iHeight', new THREE.BufferAttribute(new Float32Array(p.height), 1));
    geometry.setAttribute('iLeft', new THREE.BufferAttribute(new Float32Array(p.left), 1));
    geometry.setAttribute('iCentre', new THREE.BufferAttribute(new Float32Array(p.centre), 1));
    geometry.setAttribute('iAxis', new THREE.BufferAttribute(new Float32Array(p.axis), 2));
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 1, (Z_NEAR + Z_FAR) / 2), 80);
    this._phoneGeometry = geometry;
    this._phoneCount = p.seed.length;
    this._phoneUniforms = Object.assign({}, safeZoneUniforms, this._waveUniforms, {
      uTime: { value: 0 }, uSway: { value: 1 }, uAllUp: { value: 0 }, uZone: { value: 3 },
      uLitFraction: { value: PHONE_LIT_RANGE[0] }, uFlickerAmp: { value: 0.18 },
      uSizeScale: { value: 1 }, uPointSizeMax: { value: Math.max(1, getMaxPointSize()) },
      uPhoneColor: { value: new THREE.Color(PHONE_COLOR[0], PHONE_COLOR[1], PHONE_COLOR[2]) }
    });
    this._phoneMaterial = new THREE.ShaderMaterial({
      uniforms: this._phoneUniforms, vertexShader: phoneVertexShader, fragmentShader: phoneFragmentShader,
      transparent: true, depthWrite: false, depthTest: true, blending: THREE.AdditiveBlending
    });
    this._phones = new THREE.Points(geometry, this._phoneMaterial);
    this._phones.frustumCulled = false;
    this._scene.add(this._phones);
  }

  // ?bayfrontlayer=facets|bars|beams|lasers|pyro|crowd|city - one layer alone,
  // for measuring each on the Pi.
  _applyLayerFilter() {
    const only = this._onlyLayer;
    if (!only) return;
    const groups = {
      facets: [this._facetMesh, this._truss, this._deck].concat(this._booth || []),
      bars: [this._bars],
      beams: [this._lyres],
      lasers: [this._lasers],
      pyro: this._pyro ? [this._pyro._sparks, this._pyro._trails, this._pyro._smoke, this._co2] : [this._co2],
      crowd: [this._crowd, this._phones],
      city: [this._city]
    };
    for (const key of Object.keys(groups)) {
      for (const o of groups[key]) if (o) o.visible = key === only;
    }
  }

  _applyPalette(profile) {
    const palette = profile && profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const dominant = (palette && palette.dominantColors) || [];
    const c = (i) => vivid.length > 0 ? vivid[i % vivid.length] : FALLBACK_COLORS[i % 3];
    const hsl = { h: 0, s: 0, l: 0 };
    const lit = (hex, sMin, lLo, lHi) => { const col = new THREE.Color(hex); col.getHSL(hsl); col.setHSL(hsl.h, Math.max(hsl.s, sMin), THREE.MathUtils.clamp(hsl.l, lLo, lHi)); return col; };
    const c0 = lit(c(0), 0.6, 0.45, 0.65), c1 = lit(c(1), 0.6, 0.45, 0.65), c2 = lit(c(2), 0.5, 0.45, 0.7);
    this._facetUniforms.uColor0.value.copy(c0); this._facetUniforms.uColor1.value.copy(c1); this._facetUniforms.uColor2.value.copy(c2);
    if (this._barUniforms) this._barUniforms.uBarColor.value.copy(c1);
    if (this._diodeMaterial) this._diodeMaterial.color.copy(c1);
    this._lyreUniforms.uColorA.value.copy(c1);
    if (this._laserUniforms) { this._laserUniforms.uColorA.value.copy(lit(c(1), 0.95, 0.5, 0.6)); this._laserUniforms.uColorB.value.copy(lit(c(0), 0.95, 0.5, 0.6)); }
    if (this._pyro) this._pyro.cometColor.copy(lit(c(0), 0.8, 0.52, 0.68));
    if (this._confetti && vivid.length) this._confetti.setPalette(vivid);
    const dom = new THREE.Color(dominant.length > 0 ? dominant[0] : HAZE_FALLBACK);
    dom.getHSL(hsl);
    const haze = new THREE.Color().setHSL(hsl.h, Math.min(0.45, hsl.s), 0.085);
    if (this._crowdUniforms) { this._crowdUniforms.uHazeColor.value.copy(haze); this._crowdUniforms.uRimColor.value.copy(c0); }
    const u = this._cityUniforms;
    u.uHazeColor.value.copy(haze);
    u.uSkyHorizon.value.setHSL(hsl.h, Math.min(0.5, hsl.s), 0.05);
    u.uSkyTop.value.setHSL(hsl.h, Math.min(0.5, hsl.s), 0.012);
    u.uPal0.value.copy(c0); u.uPal1.value.copy(c1); u.uPal2.value.copy(c2);
    u.uStageGlow.value.copy(c0).multiplyScalar(0.35);
    this._applyPlateTint();
  }

  _loadArtwork(url) {
    loadArtworkTexture(url, { wrap: THREE.ClampToEdgeWrapping, mipmaps: true }, (texture) => {
      const previous = this._facetUniforms.uArtwork.value;
      this._facetUniforms.uArtwork.value = texture;
      this._facetUniforms.uHasArt.value = 1;
      this._artwork = texture;
      if (previous && previous !== this._blankTexture) previous.dispose();
    }, () => { this._facetUniforms.uHasArt.value = 0; });
  }

  // ---------------------------------------------------------------------------
  // The chases: runners on the edge graph
  // ---------------------------------------------------------------------------
  _spawnRunner(edge, fromVertex, facet) {
    for (const r of this._runners) {
      if (r.live) continue;
      r.live = true; r.edge = edge; r.dir = edge.a === fromVertex ? 1 : -1; r.t = r.dir > 0 ? 0 : 1;
      r.life = 6 + Math.floor(Math.random() * 8); r.facet = facet; r.k = 0;
      return r;
    }
    return null;
  }

  _runChases(dt, speedEdgesPerSec) {
    const level = this._barLevel, edges = this._edges, adj = this._adj;
    const decay = Math.exp(-dt * CHASE_DECAY);
    const pattern = BAR_PATTERNS[this._barPattern];
    for (let i = 0; i < level.length; i++) level[i] *= decay;

    if (pattern === 'flat') {
      for (let i = 0; i < level.length; i++) level[i] = Math.max(level[i], 0.45);
    } else if (pattern === 'alternate') {
      const parity = this._beatCount & 1;
      for (let e = 0; e < edges.length; e++) {
        if ((e & 1) !== parity) continue;
        const base = e * BAR_PIXELS;
        for (let k = 0; k < BAR_PIXELS; k++) level[base + k] = Math.max(level[base + k], 0.9);
      }
    } else {
      // Keep the runners populated: NETWORK spawns them at the diamond,
      // OUTLINE one per facet on its own edge cycle.
      let live = 0;
      for (const r of this._runners) if (r.live) live++;
      const want = pattern === 'network' ? Math.min(CHASE_RUNNERS, 8 + this._facets.length / 4) : Math.min(CHASE_RUNNERS, this._facets.length);
      if (live < want) {
        if (pattern === 'network') {
          const diamond = this._facets[0];
          const e = diamond.edges[Math.floor(Math.random() * diamond.edges.length)];
          this._spawnRunner(e, Math.random() < 0.5 ? e.a : e.b, -1);
        } else {
          const f = this._facets[Math.floor(Math.random() * this._facets.length)];
          if (f.edges && f.edges.length) this._spawnRunner(f.edges[0], f.ids[0], f.index);
        }
      }
      for (const r of this._runners) {
        if (!r.live) continue;
        const e = r.edge;
        const step = speedEdgesPerSec * dt;
        r.t += r.dir * step;
        // Light the pixels the head crossed this frame, so no pixel is skipped
        // at speed.
        const t0 = Math.max(0, Math.min(1, r.t - r.dir * step)), t1 = Math.max(0, Math.min(1, r.t));
        const lo = Math.min(t0, t1), hi = Math.max(t0, t1);
        const base = e.id * BAR_PIXELS;
        for (let k = 0; k < BAR_PIXELS; k++) {
          const tp = (k + 0.5) / BAR_PIXELS;
          if (tp >= lo - 0.03 && tp <= hi + 0.03) level[base + k] = 1;
        }
        if (r.t < 0 || r.t > 1) {
          // At the vertex: OUTLINE follows its facet round; NETWORK takes
          // another edge of the shared vertex, never back the way it came.
          const vtx = r.t > 1 ? e.b : e.a;
          let next = null;
          if (r.facet >= 0) {
            const f = this._facets[r.facet];
            r.k = (r.k + 1) % f.edges.length;
            next = f.edges[r.k];
          } else {
            const options = (adj.get(vtx) || []).filter((x) => x !== e);
            if (options.length) next = options[Math.floor(Math.random() * options.length)];
          }
          r.life--;
          if (!next || r.life <= 0) { r.live = false; continue; }
          r.edge = next;
          r.dir = next.a === vtx ? 1 : -1;
          r.t = r.dir > 0 ? 0 : 1;
        }
      }
    }
    this._barLevelAttr.needsUpdate = true;
  }

  // ---------------------------------------------------------------------------
  // The segment: what plays, and how hard
  // ---------------------------------------------------------------------------
  _resolveSegment(profile, director, progress) {
    if (this._forceSegment) return this._forceSegment;
    if (this._time < this._dropUntil) return 'drop';
    if (director.hasReliableGrid && director.mode) {
      const m = director.mode;
      if (m === 'intro') return 'intro';
      if (m === 'build') return 'build';
      if (m === 'drop') return 'chorus';
      if (m === 'breakdown') return 'verse';
      if (m === 'sustain') return 'chorus';
      if (m === 'outro') return 'outro';
    }
    if (progress < 0.08) return 'intro';
    if (progress < 0.22) return 'verse';
    if (progress < FULL_AT_PROGRESS) return 'build';
    if (progress > 0.92) return 'outro';
    return 'chorus';
  }

  _fireDrop() {
    this._dropUntil = this._time + DROP_BARS * 4 * this._beatSeconds;
    if (this._firstDropAt < 0) this._firstDropAt = this._time;
    this._lyrePatPrev = this._lyrePat; this._lyrePat = LYRE_PAT.converge; this._lyrePatSince = this._time;
    this._lyreUniforms.uBurst.value = 1;
    this._cityDropAt = this._time;
    this._pyroFlash = 1;
    if (this._pyro) {
      this._pyro.fireWall(this._pyroNozzles, PYRO_V_WALL[0], PYRO_V_WALL[1], PYRO_WALL_PER_NOZZLE);
      this._pyroDimUntil = this._time + PYRO_WALL_DIM_SECONDS;
    }
    if (this._co2Uniforms) this._co2Uniforms.uStart.value = this._time;
    this._strobeUntil = this._time + STROBE_MS / 1000;
    this._confettiShot([0, 1, 2, 3], 1.15, 1.0, true);
    this._confettiFlash = 1;
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
    this._beatSeconds = 60 / Math.max(60, bpm || 128);
    const director = profile.director || {};
    const bands = director.bands || { low: 0, mid: 0, high: 0 };
    const low = clamp01(bands.low), high = clamp01(bands.high);
    const progress = clamp01(profile.progress || 0);

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile);
    }
    if (profile.artworkUrl !== this._lastArtworkUrl) {
      this._lastArtworkUrl = profile.artworkUrl;
      this._facetUniforms.uHasArt.value = 0;
      if (profile.artworkUrl) this._loadArtwork(profile.artworkUrl);
    }

    if (!paused) {
      const dt = Math.min(deltaTime, 0.05);
      this._time += dt;
      const t = this._time;
      const beat = this._beatSeconds;

      // --- the clocks
      let beatEdge = false, barEdge = false, phraseEdge = false;
      if (profile.beatPhase != null) {
        if (this._lastBeatPhase != null && profile.beatPhase < this._lastBeatPhase) beatEdge = true;
        this._lastBeatPhase = profile.beatPhase;
      } else if (t - this._freeBeatAt >= beat) { this._freeBeatAt = t; beatEdge = true; }
      if (profile.barPhase != null) {
        if (this._lastBarPhase != null && profile.barPhase < this._lastBarPhase) barEdge = true;
        this._lastBarPhase = profile.barPhase;
      } else if (beatEdge && this._beatCount % 4 === 3) barEdge = true;
      const phrase = profile.phraseIndex;
      if (phrase != null && this._lastPhrase != null && phrase !== this._lastPhrase) phraseEdge = true;
      this._lastPhrase = phrase;
      if (beatEdge) this._beatCount++;

      // --- the drop, on the frame that crosses the mark
      const ev = director.nextEvent;
      let dropNow = false;
      if (ev && ev.type === 'drop' && ev.msUntil != null) {
        if (ev.msUntil <= dt * 500 + 30) dropNow = true;
        if (this._pendingDropMs != null && this._pendingDropMs < 400 && ev.msUntil > this._pendingDropMs + 250) dropNow = true;
        this._pendingDropMs = ev.msUntil;
      } else {
        if (this._pendingDropMs != null && this._pendingDropMs < 400) dropNow = true;
        this._pendingDropMs = null;
      }
      if (director.hasReliableGrid && director.mode === 'drop' && this._lastMode !== 'drop') dropNow = true;
      this._lastMode = director.mode;
      if ((dropNow && t - this._lastDropAt > 2.0) || this._forceDrop) {
        this._forceDrop = false; this._lastDropAt = t; this._fireDrop();
      }

      // --- the segment
      const seg = this._resolveSegment(profile, director, progress);
      if (seg !== this._segment) { this._segment = seg; this._segmentSince = t; }
      const full = this._firstDropAt >= 0 || (!director.hasReliableGrid && progress >= FULL_AT_PROGRESS) || this._forceSegment === 'chorus' || this._forceSegment === 'drop';
      // lyreDown: the patterns that descend on the crowd (RAIN, WAVE), cycled
      // on the phrases; lyrePat: one pattern held. Two directions alternate
      // over the track: down onto the public in VERSE and CHORUS, up to the
      // sky in BUILD and DROP.
      // lasers: gain, and laserMode 0 fan / 1 scan / null alternating.
      const S = {
        intro: { facets: 'diamond', chase: 0.5, lyres: 0, lyrePat: null, lyreDown: false, lasers: 0, laserMode: 0, laserSpeed: 0.5, crowd: 0.35, bright: 0.55 },
        verse: { facets: 'all', chase: 1.0, lyres: 0.7, lyrePat: null, lyreDown: true, lasers: 0.35, laserMode: 0, laserSpeed: 0.5, crowd: 0.7, bright: 0.7 },
        build: { facets: 'all', chase: 1.8, lyres: 0.85, lyrePat: LYRE_PAT.fan, lyreDown: false, lasers: 0.7, laserMode: 1, laserSpeed: 1, crowd: 0.85, bright: 0.8, content: CONTENT.pulse },
        drop: { facets: 'all', chase: 2.4, lyres: 1.0, lyrePat: LYRE_PAT.converge, lyreDown: false, lasers: 1.0, laserMode: 1, laserSpeed: 1, crowd: 1.0, bright: 1.0 },
        chorus: { facets: 'all', chase: 1.6, lyres: 1.0, lyrePat: null, lyreDown: true, lasers: 1.0, laserMode: null, laserSpeed: 1, crowd: 1.0, bright: 0.9 },
        outro: { facets: 'diamond', chase: 0.5, lyres: 0, lyrePat: null, lyreDown: false, lasers: 0, laserMode: 0, laserSpeed: 0.5, crowd: 0.4, bright: 0.55 }
      }[seg];
      const LYRE_DOWN = [LYRE_PAT.rain, LYRE_PAT.wave];

      // Facet visibility, faded on the CPU.
      for (let i = 0; i < this._facets.length; i++) {
        const want = S.facets === 'all' || i === 0 ? 1 : 0;
        const cur = this._facetVisible[i];
        const next = cur + (want - cur) * Math.min(1, dt * 1.6);
        if (Math.abs(next - cur) > 1e-4) {
          this._facetVisible[i] = next;
          const f = this._facets[i];
          const base = this._facets.slice(0, i).reduce((s, x) => s + x.ids.length, 0);
          for (let k = 0; k < f.ids.length; k++) this._facetOn[base + k] = next;
          this._facetGeometry.attributes.aOn.needsUpdate = true;
        }
      }

      // --- content mode: on the phrases, a segment's own, or the debug
      if (phraseEdge) this._cityWaveAt = t;
      if (phraseEdge) {
        // A comet from an outer nozzle on every phrase, in the palette.
        if (this._pyro && seg !== 'intro' && seg !== 'outro') this._pyro.fireComet(this._pyroNozzles[(this._contentTurn & 1) ? 5 : 0]);
        if (this._confetti && seg !== 'intro' && seg !== 'outro' && !(t < this._dropUntil)) { const a = this._confettiTurn++ % 4; this._confettiShot([a, (a + 2) % 4], 1.0, 0.45 + 0.55 * progress, true); }
        this._contentTurn = (this._contentTurn + 1) % CONTENT_CYCLE.length;
        this._barPattern = (this._barPattern + 1) % BAR_PATTERNS.length;
        if (S.lyrePat == null) {
          this._lyreTurn = (this._lyreTurn + 1) % 6;
          const cycle = S.lyreDown ? LYRE_DOWN : LYRE_CYCLE;
          this._lyrePatPrev = this._lyrePat; this._lyrePat = cycle[this._lyreTurn % cycle.length]; this._lyrePatSince = t;
        }
        this._laserMode = S.laserMode != null ? S.laserMode : 1 - this._laserMode;
      }
      let wantContent = this._forceContent != null ? this._forceContent : (S.content != null ? S.content : CONTENT_CYCLE[this._contentTurn]);
      if (wantContent === CONTENT.artwork && !this._facetUniforms.uHasArt.value) wantContent = CONTENT.solid;
      if (wantContent !== this._content) { this._contentPrev = this._content; this._content = wantContent; this._contentMix = 0; }
      this._contentMix = Math.min(1, this._contentMix + dt / (2 * beat));
      if (S.lyrePat != null && S.lyrePat !== this._lyrePat) { this._lyrePatPrev = this._lyrePat; this._lyrePat = S.lyrePat; this._lyrePatSince = t; }
      if (S.lyrePat == null && S.lyreDown && LYRE_DOWN.indexOf(this._lyrePat) < 0) { this._lyrePatPrev = this._lyrePat; this._lyrePat = LYRE_DOWN[0]; this._lyrePatSince = t; }
      if (S.laserMode != null) this._laserMode = S.laserMode;
      this._laserSpeed = S.laserSpeed;

      // --- the beat: chases and pulse, lyre notch, video noise on bars
      if (beatEdge) {
        this._lyreStep++; this._lyreStepAt = t;
        this._pulseT = Math.floor(this._pulseT) + 1;
        if (this._lyrePat === LYRE_PAT.converge) {
          this._lyreUniforms.uConvergePrev.value.copy(this._lyreUniforms.uConverge.value);
          this._lyreUniforms.uConverge.value.set((Math.random() - 0.5) * 30, 55 + Math.random() * 15, -25 + (Math.random() - 0.5) * 10);
        }
      }
      this._pulseT += dt / beat;
      // --- the bar: strobe, CO2 and a short gerb after the first drop, a
      // crowd wave, the phones' zones
      if (barEdge) {
        if (this._confetti && full && (seg === 'chorus' || seg === 'drop') && !(t < this._dropUntil)) this._confettiShot([this._confettiTurn++ % 4], 0.75 + 0.5 * low, 0.45 + 0.55 * progress, false);
        this._cityPulse = 1;
        this._strobeUntil = t + STROBE_MS / 1000;
        this._noise = 1;
        this._zoneStep = (this._zoneStep + 1) % PHONE_ZONE_STEPS;
        const w = this._waves[this._waveTurn]; this._waveTurn = (this._waveTurn + 1) % WAVE_SLOTS; w.set(t, 0, 0, 0);
        if (full && this._co2Uniforms && !(t < this._dropUntil)) this._co2Uniforms.uStart.value = t;
        // A short gerb on every bar from VERSE on, a pair on the bars of a
        // CHORUS - the fire is part of the show, not a drop-only event.
        if (this._pyro && seg !== 'intro' && seg !== 'outro' && !(t < this._dropUntil)) {
          const pairs = (seg === 'chorus' || seg === 'drop') ? 2 : 1;
          const power = seg === 'build' ? 1.35 : 1.0;
          const perShot = Math.round(PYRO_BEAT_PARTICLES * 0.8 * this._pyro.scale() * 1.3 / PYRO_TRAIN_SHOTS);
          for (let k = 0; k < pairs; k++) {
            const n = this._pyroNozzles[[0, 5, 2, 3, 1, 4][this._pyroTurn++ % 6]];
            for (let j = 0; j < PYRO_TRAIN_SHOTS; j++) this._pyroQueue.push({ n, at: t + j * PYRO_TRAIN_GAP, vlo: PYRO_V_SHORT[0] * power, vhi: PYRO_V_SHORT[1] * power, count: perShot });
          }
        }
      }
      this._noise = Math.max(0, this._noise - dt / (0.35 * beat));
      if (this._forceChase) { this._forceChase = false; for (let k = 0; k < 8; k++) { const d = this._facets[0]; const e = d.edges[k % d.edges.length]; this._spawnRunner(e, e.a, -1); } }

      // The strobe, with StrobeWall's filament: up in ~45 ms, down in ~180.
      const on = t < this._strobeUntil ? 1 : 0;
      this._strobeTemp += (on - this._strobeTemp) * Math.min(1, (on > this._strobeTemp ? STROBE_HEAT : STROBE_COOL) * dt);

      if (this._bars) this._runChases(dt, CHASE_EDGES_PER_BEAT / beat * S.chase * (0.6 + 0.8 * high));

      // --- the drop's phases on the facets: cover on the diamond and white
      // elsewhere for a bar, then the inverse.
      if (t < this._dropUntil) {
        const into = (t - (this._dropUntil - DROP_BARS * 4 * beat)) / (4 * beat);
        this._dropPhase = into < 1 ? 1 : 2;
      } else this._dropPhase = 0;
      this._lyreUniforms.uBurst.value *= Math.exp(-dt * 2.2);

      // --- pyro, lasers, lyres gains
      if (this._pyro) {
        const q = this._pyroQueue;
        for (let i = q.length - 1; i >= 0; i--) {
          const sh = q[i];
          if (t < sh.at) continue;
          this._pyro.fireGerb(sh.n, sh.vlo, sh.vhi, sh.count, sh.n.x < 0 ? -PYRO_NOZZLE_TILT : PYRO_NOZZLE_TILT, 0);
          this._pyroFlash = Math.max(this._pyroFlash, 0.55);
          q.splice(i, 1);
        }
        this._pyro.update(dt, high, progress);
      }
      if (this._confetti) {
        this._confettiFlash = Math.max(0, this._confettiFlash - dt);
        this._confetti.setFlash(this._confettiFlash > 0.85 ? 0 : this._confettiFlash);
        this._placeConfettiLights(t);
        this._confetti.update(dt, high);
      }
      this._lyreGainTarget = S.lyres * this._layout.lyres / 32;
      // 0.85 at most: three fans of twelve toward the camera at full gain put
      // the chorus frame at a mean of 101 - the sky itself was pink. From
      // VERSE on, weak; only INTRO and OUTRO go without.
      this._laserGainTarget = 0.85 * S.lasers;
      this._crowdLightTarget = S.crowd;
      this._brightTarget = S.bright;
      this._chaseSpeed = S.chase;
    }

    // --- uniforms
    const t = this._time, beat = this._beatSeconds;
    const pulse = director.pulse ? director.pulse(0.35, 10) : 1;
    const canvasPixelHeight = typeof window !== 'undefined' ? window.innerHeight * Math.min(window.devicePixelRatio || 1, 2) : 720;
    const tanHalf = Math.tan(THREE.MathUtils.degToRad(this._camera.fov) / 2);
    const pixelScale = (canvasPixelHeight / 2) / tanHalf;
    const ease = Math.min(1, (t - this._lyreStepAt) * 1000 / MOVE_MS);
    const fade = Math.min(1, (t - this._lyrePatSince) / (PATTERN_FADE_BEATS * beat));

    const fu = this._facetUniforms;
    fu.uMode.value = this._content; fu.uModePrev.value = this._contentPrev; fu.uModeMix.value = this._contentMix;
    fu.uDrop.value = this._dropPhase; fu.uPulseT.value = this._pulseT; fu.uTime.value = t;
    const wallDim = t < (this._pyroDimUntil || -1e9) ? PYRO_WALL_DIM : 1;
    this._pyroWallDim = (this._pyroWallDim == null ? 1 : this._pyroWallDim) + (wallDim - (this._pyroWallDim == null ? 1 : this._pyroWallDim)) * Math.min(1, deltaTime * (wallDim < 1 ? 12 : 2.5));
    fu.uBrightness.value = (this._brightTarget != null ? this._brightTarget : 0.8) * this._pyroWallDim; fu.uNoise.value = this._noise;
    if (this._barUniforms) { this._barUniforms.uStrobe.value = this._strobeTemp; this._barUniforms.uSizeScale.value = canvasPixelHeight / 1080; }
    const lu = this._lyreUniforms;
    lu.uPat.value = this._lyrePat; lu.uPatPrev.value = this._lyrePatPrev; lu.uPatMix.value = fade;
    lu.uStep.value = this._lyreStep; lu.uStepMix.value = ease * ease * (3 - 2 * ease);
    lu.uSpread.value = 0.7 + 0.6 * low;
    lu.uMasterGain.value += ((this._lyreGainTarget || 0) - lu.uMasterGain.value) * Math.min(1, deltaTime * 2);
    lu.uWhite.value = this._dropPhase > 0 ? 0.8 : 0;
    lu.uPixelScale.value = pixelScale; lu.uPulse.value = pulse;
    if (this._laserUniforms) {
      const la = this._laserUniforms;
      la.uLaserMode.value = this._laserMode;
      la.uLaserPhase.value = (this._pulseT / (4 / (this._laserSpeed || 1))) % 1;
      la.uMasterGain.value += ((this._laserGainTarget || 0) - la.uMasterGain.value) * Math.min(1, deltaTime * 3);
      la.uPixelScale.value = pixelScale; la.uTime.value = t; la.uPulse.value = pulse;
    }
    if (this._co2Uniforms) { this._co2Uniforms.uTime.value = t; this._co2Uniforms.uSizeScale.value = canvasPixelHeight / 1080; }
    if (this._pyro) this._pyro.setResolution(canvasPixelHeight * this._camera.aspect, canvasPixelHeight, Math.max(PYRO_GRAIN_MIN, (canvasPixelHeight / 420) * PYRO_SCALE / (tanHalf * this._pyroDepth) * PYRO_GRAIN_GAIN));
    if (this._waveUniforms) { this._waveUniforms.uWaveSeconds.value = WAVE_BEATS * beat; this._waveUniforms.uHoldSeconds.value = WAVE_HOLD_BEATS * beat; }
    const allUp = t < this._dropUntil ? 1 : 0;
    if (this._crowdUniforms) {
      const u = this._crowdUniforms;
      u.uTime.value = t; u.uSway.value = 0.6 + 0.9 * low; u.uAllUp.value = allUp; u.uRimPulse.value = pulse;
      u.uPixelScale.value = pixelScale;
      u.uCrowdLight.value += ((this._crowdLightTarget || 1) - u.uCrowdLight.value) * Math.min(1, deltaTime * 1.5);
      if (this._laserUniforms) {
        const la = this._laserUniforms;
        const ph = la.uLaserPhase.value * Math.PI * 2;
        const pitch = this._laserMode === 0 ? LASER_PITCH_FAN : LASER_PITCH_SCAN[0] + (LASER_PITCH_SCAN[1] - LASER_PITCH_SCAN[0]) * (0.5 - 0.5 * Math.cos(ph));
        u.uLaserSheet.value.set(DIAMOND_CENTER_Y + OCTAGON_R_OUT + 1.5, pitch, la.uMasterGain.value * 0.9, STAGE_Z - 2);
        u.uLaserColor.value.copy(la.uColorA.value);
      }
    }
    if (this._phoneUniforms) {
      const u = this._phoneUniforms;
      u.uTime.value = t; u.uSway.value = 0.6 + 0.9 * low; u.uAllUp.value = allUp; u.uZone.value = this._zoneStep;
      u.uLitFraction.value = (PHONE_LIT_RANGE[0] + (PHONE_LIT_RANGE[1] - PHONE_LIT_RANGE[0]) * progress) * (this._crowdLightTarget || 1);
      u.uFlickerAmp.value = 0.10 + 0.22 * high; u.uSizeScale.value = canvasPixelHeight / 1080;
    }
    this._cityPulse = (this._cityPulse || 0) * Math.exp(-deltaTime * 2.0);
    this._cityUniforms.uTime.value = t; this._cityUniforms.uHigh.value = high; this._cityUniforms.uBarPulse.value = this._cityPulse;
    if (this._plate) {
      const ev = director.nextEvent;
      const dropMs = ev && ev.type === 'drop' && ev.msUntil != null ? ev.msUntil : null;
      if (!paused) this._updateCity(t, beat, this._segment, dropMs);
      // the scene's light on the city: the lyres' colour at their gain, the
      // strobe white, the pyro's comet colour on a flash that decays
      this._pyroFlash *= Math.exp(-deltaTime * 6.0);
      const sl = this._plateUniforms.uSceneLight.value;
      const lc = this._lyreUniforms.uColorA.value, lg = this._lyreUniforms.uMasterGain.value;
      const pc = this._pyro ? this._pyro.cometColor : lc;
      sl.set(lc.r * lg * 0.6 + this._strobeTemp * 0.8 + pc.r * this._pyroFlash * 1.2,
             lc.g * lg * 0.6 + this._strobeTemp * 0.8 + pc.g * this._pyroFlash * 1.2,
             lc.b * lg * 0.6 + this._strobeTemp * 0.8 + pc.b * this._pyroFlash * 1.2);
      this._plateUniforms.uTime.value = t;
    }
  }

  getLightRig() { return null; }

  dispose() {
    this._disposed = true;
    this._scene.fog = this._previousFog || null;
    if (this._plate) { this._scene.remove(this._plate); this._plateGeometry.dispose(); this._plateMaterial.dispose(); this._cityLitTex.dispose(); this._plate = null; }
    if (this._hazeLayer) { this._scene.remove(this._hazeLayer); this._hazeLayer.geometry.dispose(); this._hazeMaterial.dispose(); this._hazeLayer = null; }
    for (const o of [this._facetMesh, this._truss, this._deck, this._bars, this._lyres, this._lasers, this._co2, this._city, this._crowd, this._phones].concat(this._booth || [])) {
      if (o) { o.onBeforeRender = null; this._scene.remove(o); }
    }
    if (this._boothGeometries) for (const g of this._boothGeometries) g.dispose();
    if (this._boothMaterials) for (const m of this._boothMaterials) m.dispose();
    for (const g of [this._facetGeometry, this._trussGeometry, this._deckGeometry, this._barGeometry, this._lyreGeometry, this._laserGeometry, this._co2Geometry, this._cityGeometry, this._crowdGeometry, this._phoneGeometry]) if (g) g.dispose();
    for (const m of [this._facetMaterial, this._trussMaterial, this._deckMaterial, this._barMaterial, this._lyreMaterial, this._laserMaterial, this._co2Material, this._cityMaterial, this._crowdMaterial, this._phoneMaterial]) if (m) m.dispose();
    if (this._artwork) this._artwork.dispose();
    if (this._blankTexture) this._blankTexture.dispose();
    if (this._pyro) { this._pyro.dispose(); this._pyro = null; }
    if (this._confetti) { this._confetti.dispose(); this._confetti = null; }
    this._facetMesh = this._bars = this._lyres = this._lasers = this._co2 = this._city = this._crowd = this._phones = null;
  }
}

const _size = new THREE.Vector2();
